// src/components/forms/FormViewer.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Share2, Download, ArrowLeft, Globe, Lock, Pencil, ListTree } from 'lucide-react';
import html2canvas from 'html2canvas';
import { PDFDocument, StandardFonts, rgb, PDFName, PDFArray, PDFString } from 'pdf-lib';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { formService } from '../../services/formService';
import SmartSensorField from './SmartSensorField';

const A4_WIDTH_PX = 794;
const A4_HEIGHT_PX = 1123;
const HEADER_SPACE_PX = 56;
const FOOTER_SPACE_PX = 56;
const TEXT_LAYOUT_SAFETY_PX = 28;
const SMART_PAGE_PADDING_TOP_PX = 24;
const SMART_PAGE_PADDING_BOTTOM_PX = 72;
const PAGE_INNER_WIDTH_PX = A4_WIDTH_PX - 96;
const GRID_GAP_PX = 8;
const A4_WIDTH_PT = 595.28;
const A4_HEIGHT_PT = 841.89;
const PARROTDOCS_URL = 'https://parrotdocs.co.za/forms';
const EXPORT_SIGNATURE_HTML = `<div data-export-signature="true" style="margin-top:24px;padding-top:16px;border-top:1px solid #e2e8f0;display:flex;align-items:flex-end;justify-content:space-between;gap:16px;font-size:12px;color:#475569;"><div style="flex:1;"><div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;color:#64748b;">Signature</div><div style="margin-top:8px;border-bottom:1px solid #334155;height:22px;"></div></div><div style="width:180px;"><div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;color:#64748b;">Date</div><div style="margin-top:8px;border-bottom:1px solid #334155;height:22px;"></div></div></div><div style="margin-top:12px;font-size:11px;color:#64748b;text-align:center;"><a href="${PARROTDOCS_URL}" target="_blank" rel="noreferrer" style="color:#232753;text-decoration:underline;">${PARROTDOCS_URL}</a></div>`;

const DEFAULT_SMART_DOC_CONFIG = {
  headerText: 'Smart Processed Document',
  footerText: 'ParrotDocs Smart Processing',
  showHeader: true,
  showFooter: true,
  showNavigationMenu: false,
  navigationMenuTitle: 'Document Navigation'
};

const textSizeClassMap = {
  xs: 'text-xs',
  sm: 'text-sm',
  base: 'text-base',
  lg: 'text-lg',
  xl: 'text-xl',
  '2xl': 'text-2xl',
  '3xl': 'text-3xl'
};

const widthClassMap = {
  full: 'col-span-12',
  'two-thirds': 'col-span-12 md:col-span-8',
  half: 'col-span-12 md:col-span-6',
  third: 'col-span-12 md:col-span-4'
};
const widthSpanMap = {
  full: 12,
  'two-thirds': 8,
  half: 6,
  third: 4
};

const buildNavigationItems = (items = []) =>
  flattenSmartComponents(items)
    .filter((component) => (
      ['heading', 'sectionDivider'].includes(component?.type) &&
      `${component?.props?.content || ''}`.trim()
    ))
    .map((component, index) => ({
      id: component.id,
      label: `${component.props.content}`.trim(),
      type: component.type,
      level: component.props.level || (component.type === 'heading' ? 2 : 3),
      order: index + 1
    }));

const SmartNavigationPanel = ({ items = [], title = 'Document Navigation', fixedClass = 'lg:left-6 lg:top-24 lg:bottom-6' }) => (
  <aside className={`w-full shrink-0 lg:fixed lg:z-30 lg:w-72 ${fixedClass}`}>
    <nav className="max-h-full overflow-y-auto rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-[0.18em] text-[#232753]">
        <ListTree className="h-4 w-4 text-[#4f46e5]" />
        <span>{title || 'Document Navigation'}</span>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-slate-500">No document sections are available.</p>
      ) : (
        <div className="space-y-2">
          {items.map((item) => (
            <a
              key={`smart-nav-panel-${item.id}`}
              href={`#smart-section-${item.id}`}
              className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-[#232753] hover:border-[#4f46e5] hover:bg-[#eef2ff]"
            >
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#eef2ff] text-xs text-[#4f46e5]">
                {item.order}
              </span>
              <span className="min-w-0 truncate">{item.label}</span>
            </a>
          ))}
        </div>
      )}
    </nav>
  </aside>
);

const getTextSizeClass = (size, fallback = 'text-sm') => textSizeClassMap[size] || fallback;
const getWidthClass = (width) => widthClassMap[width] || widthClassMap.full;
const getWidthSpan = (width) => widthSpanMap[width] || widthSpanMap.full;
const getPageContentHeight = (showHeader = true, showFooter = true) =>
  A4_HEIGHT_PX - (showHeader ? HEADER_SPACE_PX : 0) - (showFooter ? FOOTER_SPACE_PX : 0);
const textSizePxMap = {
  xs: 12,
  sm: 14,
  base: 16,
  lg: 18,
  xl: 20,
  '2xl': 24,
  '3xl': 30
};

const stripMarkdown = (value = '') =>
  `${value}`
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1')
    .replace(/[*_~>#-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const charsPerLineForWidth = (width = 'full', fontPx = 14) => {
  const baseMap = {
    full: 78,
    'two-thirds': 52,
    half: 38,
    third: 24
  };
  const base = baseMap[width] || baseMap.full;
  const adjustment = Math.round((16 - fontPx) * 1.3);
  return Math.max(12, base + adjustment);
};

const estimateRenderedLineCount = (text, width = 'full', fontPx = 14) => {
  const charsPerLine = charsPerLineForWidth(width, fontPx);
  const normalized = `${text || ''}`.replace(/\r\n/g, '\n');
  const segments = normalized.split('\n');

  return segments.reduce((total, segment) => {
    const content = stripMarkdown(segment);
    return total + Math.max(1, Math.ceil((content.length || 0) / charsPerLine));
  }, 0);
};

const estimateTextHeight = (text, width = 'full', textSize = 'base', extra = 0) => {
  const fontPx = textSizePxMap[textSize] || 16;
  const lines = Math.max(1, estimateRenderedLineCount(text || ' ', width, fontPx));
  return Math.ceil(lines * (fontPx * 1.6) + extra);
};

const orderedListPattern = /^(\d+)\.\s+(.+)$/;
const unorderedListPattern = /^[-*]\s+(.+)$/;
const thematicBreakPattern = /^(-{3,}|\*{3,}|_{3,})$/;

const estimateTextBlockExtraHeight = (text = '') => {
  const normalized = `${text || ''}`.replace(/\r\n/g, '\n').trim();
  if (!normalized) return 24;

  const lines = normalized.split('\n').map((line) => line.trim()).filter(Boolean);
  const headingCount = lines.filter((line) => /^#{1,6}\s+/.test(line)).length;
  const orderedCount = lines.filter((line) => orderedListPattern.test(line)).length;
  const unorderedCount = lines.filter((line) => unorderedListPattern.test(line)).length;
  const blockCount = normalized.split(/\n\s*\n/).map((chunk) => chunk.trim()).filter(Boolean).length;

  return 24 + (headingCount * 10) + ((orderedCount + unorderedCount) * 4) + (Math.max(0, blockCount - 1) * 10);
};

const estimateSmartComponentHeight = (component) => {
  const props = component?.props || {};
  const width = props.width || 'full';

  switch (component.type) {
    case 'heading': {
      const size = props.textSize || (props.level === 1 ? '3xl' : props.level === 2 ? '2xl' : 'xl');
      return estimateTextHeight(props.content || 'Heading', width, size, 24);
    }
    case 'textBlock': {
      const text = getTextBlockPlainText(props) || 'Text';
      return estimateTextHeight(text, width, props.textSize || 'base', estimateTextBlockExtraHeight(text));
    }
    case 'richContent': {
      return measureRichContentHeight(props);
    }
    case 'sectionDivider':
      return Math.max(32, Number(props.spacingTop ?? 24) + Number(props.spacingBottom ?? 24) + (props.showLabel !== false && props.content ? 36 : 12));
    case 'navigationMenu':
      return Math.max(96, 68 + ((Array.isArray(props.items) ? props.items.length : 0) * 28));
    case 'image':
      return Math.max(120, Number(props.imgHeight) || 220) + 12;
    case 'textInput':
    case 'datePicker':
    case 'dropdown':
      return 84;
    case 'textareaInput':
      return 96 + (Math.max(0, Number(props.rows || 3) - 3) * 18);
    case 'checkbox':
      return 48;
    case 'radio':
    case 'checklist':
      return 56 + ((Array.isArray(props.options) ? props.options.length : 0) * 28);
    case 'progress':
      return 54;
    case 'rating':
      return 60;
    case 'trafficLightStatus':
      return 78;
    case 'submitButton':
      return 60;
    case 'pageBreak':
      return 0;
    default:
      return 72;
  }
};

const splitTextBlockForHeight = (component, availableHeight, fallbackHeight) => {
  const props = component?.props || {};
  const width = props.width || 'full';
  const textSize = props.textSize || 'base';
  const fontPx = textSizePxMap[textSize] || 16;
  const perLineHeight = fontPx * 1.6;
  const overhead = 24;
  const maxHeightRaw = availableHeight > overhead + perLineHeight ? availableHeight : fallbackHeight;
  const maxHeight = Math.max(overhead + perLineHeight, maxHeightRaw - TEXT_LAYOUT_SAFETY_PX);
  const availableLines = Math.max(1, Math.floor((maxHeight - overhead) / perLineHeight));
  const fullPageLines = Math.max(
    1,
    Math.floor((Math.max(overhead + perLineHeight, fallbackHeight - TEXT_LAYOUT_SAFETY_PX) - overhead) / perLineHeight)
  );
  const fullContent = getTextBlockSource(props);
  const source = getTextBlockPlainText(props);
  const fragmentStart = Number.isFinite(props.fragmentStart) ? props.fragmentStart : 0;

  if (!source.trim()) return { first: component, rest: null };
  if (estimateRenderedLineCount(source, width, fontPx) <= availableLines) return { first: component, rest: null };

  const paragraphMatch = source.match(/[\s\S]*?(?:\n\s*\n|$)/);
  const firstParagraph = paragraphMatch?.[0]?.trim() || '';
  if (firstParagraph) {
    const firstParagraphLines = estimateRenderedLineCount(firstParagraph, width, fontPx);
    const canFitOnFreshPage = firstParagraphLines <= fullPageLines;
    const isCurrentPageConstrained = availableHeight < fallbackHeight;
    if (isCurrentPageConstrained && firstParagraphLines > availableLines && canFitOnFreshPage) {
      return { first: null, rest: component };
    }
  }

  const { firstText, restText } = splitTextByRenderedLines(source, availableLines, width, fontPx);
  if (!firstText.trim() || !restText.trim()) return { first: null, rest: component };

  return {
    first: {
      ...component,
      props: {
        ...props,
        content: firstText,
        contentHtml: markdownToHtml(firstText),
        isSplitFragment: true,
        fullContent,
        fragmentStart,
        fragmentEnd: fragmentStart + firstText.length
      }
    },
    rest: {
      ...component,
      props: {
        ...props,
        content: restText,
        contentHtml: markdownToHtml(restText),
        isSplitFragment: true,
        fullContent,
        fragmentStart: fragmentStart + firstText.length,
        fragmentEnd: fragmentStart + firstText.length + restText.length
      }
    }
  };
};

const createRichContentFragment = (component, contentHtml, media) => {
  const safeHtml = `${contentHtml || ''}`;
  return {
    ...component,
    props: {
      ...(component.props || {}),
      contentHtml: safeHtml,
      content: htmlToPlainText(safeHtml),
      media: Array.isArray(media) ? media : [],
      suppressEmptyContent: !safeHtml.trim(),
      isSplitFragment: true
    }
  };
};

const paginateSmartComponents = (components, pageContentHeight = getPageContentHeight()) => {
  const pages = [];
  let currentItems = [];
  let remainingHeight = pageContentHeight;
  let fragmentCursor = 0;
  let currentRowSpan = 0;
  let currentRowHeight = 0;

  const pushPage = () => {
    pages.push(currentItems);
    currentItems = [];
    remainingHeight = pageContentHeight;
    currentRowSpan = 0;
    currentRowHeight = 0;
  };

  const pushEntry = (entry, height) => {
    const span = getWidthSpan(entry?.renderComponent?.props?.width || entry?.component?.props?.width || 'full');
    const heightDelta = currentRowSpan === 0 ? height : Math.max(0, height - currentRowHeight);
    currentItems.push(entry);
    remainingHeight -= heightDelta;
    currentRowSpan += span;
    currentRowHeight = Math.max(currentRowHeight, height);

    if (currentRowSpan >= 12) {
      currentRowSpan = 0;
      currentRowHeight = 0;
    }
  };

  components.forEach((component) => {
    if (component.type === 'pageBreak') {
      pushPage();
      return;
    }

    let pending = {
      component,
      renderComponent: component,
      isFragment: false
    };

    while (pending) {
      const widthSpan = getWidthSpan(pending.renderComponent?.props?.width || component?.props?.width || 'full');
      if (currentRowSpan > 0 && currentRowSpan + widthSpan > 12) {
        currentRowSpan = 0;
        currentRowHeight = 0;
      }

      const estimatedHeight = estimateSmartComponentHeight(pending.renderComponent);
      const heightDelta = currentRowSpan === 0 ? estimatedHeight : Math.max(0, estimatedHeight - currentRowHeight);
      if (heightDelta <= remainingHeight) {
        pushEntry(
          {
            ...pending,
            key: `${component.id}-${fragmentCursor}`
          },
          estimatedHeight
        );
        fragmentCursor += 1;
        pending = null;
        continue;
      }

      if (currentRowSpan > 0) {
        currentRowSpan = 0;
        currentRowHeight = 0;
        continue;
      }

      if (component.type === 'textBlock') {
        const split = splitTextBlockForHeight(pending.renderComponent, remainingHeight, pageContentHeight);
        if (split.first && split.rest) {
          pushEntry(
            {
              key: `${component.id}-${fragmentCursor}`,
              component,
              renderComponent: split.first,
              isFragment: true
            },
            estimateSmartComponentHeight(split.first)
          );
          fragmentCursor += 1;
          pushPage();
          pending = {
            component,
            renderComponent: split.rest,
            isFragment: true
          };
          continue;
        }
      }

      if (component.type === 'richContent') {
        const pendingProps = pending.renderComponent?.props || component.props || {};
        const pendingHtml = getRichContentHtml(pendingProps);
        const pendingMedia = getRichMediaItems(pendingProps);
        const htmlHeight = pendingHtml.trim() ? Math.max(72, measureRichContentHtmlHeight(pendingHtml, pendingProps)) : 0;
        const split = pendingHtml.trim()
          ? splitRichHtmlToFitHeight(pendingHtml, remainingHeight, pendingProps)
          : { firstHtml: '', restHtml: '' };

        if (split.firstHtml && split.restHtml) {
          const firstFragment = createRichContentFragment(component, split.firstHtml, []);
          pushEntry(
            {
              key: `${component.id}-${fragmentCursor}`,
              component,
              renderComponent: firstFragment,
              isFragment: true
            },
            estimateSmartComponentHeight(firstFragment)
          );
          fragmentCursor += 1;
          pushPage();
          pending = {
            component,
            renderComponent: createRichContentFragment(component, split.restHtml, pendingMedia),
            isFragment: true
          };
          continue;
        }

        if (htmlHeight <= remainingHeight && pendingMedia.length > 0) {
          let usedHeight = htmlHeight;
          let mediaCount = 0;
          while (mediaCount < pendingMedia.length) {
            const mediaHeight = getRichMediaHeight(pendingMedia[mediaCount]) + 24;
            if (usedHeight + mediaHeight > remainingHeight) break;
            usedHeight += mediaHeight;
            mediaCount += 1;
          }

          if (mediaCount > 0 || pendingHtml.trim()) {
            const firstFragment = createRichContentFragment(
              component,
              pendingHtml,
              pendingMedia.slice(0, mediaCount)
            );
            pushEntry(
              {
                key: `${component.id}-${fragmentCursor}`,
                component,
                renderComponent: firstFragment,
                isFragment: true
              },
              estimateSmartComponentHeight(firstFragment)
            );
            fragmentCursor += 1;

            if (mediaCount < pendingMedia.length) {
              pushPage();
              pending = {
                component,
                renderComponent: createRichContentFragment(component, '', pendingMedia.slice(mediaCount)),
                isFragment: true
              };
            } else {
              pending = null;
            }
            continue;
          }
        }
      }

      if (currentItems.length === 0) {
        pushEntry(
          {
            ...pending,
            key: `${component.id}-${fragmentCursor}`
          },
          estimatedHeight
        );
        fragmentCursor += 1;
        pending = null;
        continue;
      }

      pushPage();
    }
  });

  if (currentItems.length > 0 || pages.length === 0) {
    pages.push(currentItems);
  }

  return pages;
};

const escapeHtml = (value) =>
  `${value ?? ''}`
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const renderInlineMarkdown = (text) => {
  let html = escapeHtml(text);
  html = html.replace(/`([^`]+)`/g, '<code class="rounded bg-slate-100 px-1 py-0.5 text-[0.92em]">$1</code>');
  html = html.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer" class="text-[#4f46e5] underline">$1</a>');
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/__([^_]+)__/g, '<strong>$1</strong>');
  html = html.replace(/\*([^*]+)\*/g, '<em>$1</em>');
  html = html.replace(/_([^_]+)_/g, '<em>$1</em>');
  html = html.replace(/~~([^~]+)~~/g, '<del>$1</del>');
  return html;
};

const splitMarkdownTableRow = (line = '') => {
  let source = `${line}`.trim();
  if (source.startsWith('|')) source = source.slice(1);
  if (source.endsWith('|') && !source.endsWith('\\|')) source = source.slice(0, -1);
  const cells = [];
  let cell = '';
  let inCode = false;
  for (let index = 0; index < source.length; index += 1) {
    const char = source[index];
    if (char === '\\' && source[index + 1] === '|') {
      cell += '|';
      index += 1;
      continue;
    }
    if (char === '`') inCode = !inCode;
    if (char === '|' && !inCode) {
      cells.push(cell.trim());
      cell = '';
      continue;
    }
    cell += char;
  }
  cells.push(cell.trim());
  return cells;
};

const getMarkdownTableAlignments = (line = '') => {
  if (!`${line}`.includes('|')) return null;
  const cells = splitMarkdownTableRow(line);
  if (!cells.length || cells.some((cell) => !/^:?-{3,}:?$/.test(cell.replace(/\s+/g, '')))) return null;
  return cells.map((cell) => {
    const marker = cell.replace(/\s+/g, '');
    if (marker.startsWith(':') && marker.endsWith(':')) return 'center';
    if (marker.endsWith(':')) return 'right';
    return 'left';
  });
};

const renderMarkdownTable = (headerCells, alignments, bodyRows) => {
  const columnCount = headerCells.length;
  const renderCell = (cell, index, tag) => (
    `<${tag} style="text-align:${alignments[index] || 'left'};">${renderInlineMarkdown(cell || '')}</${tag}>`
  );
  const normalizedRows = bodyRows.map((row) => Array.from({ length: columnCount }, (_, index) => row[index] || ''));
  return (
    '<div class="markdown-table-wrap">' +
      '<table>' +
        `<thead><tr>${headerCells.map((cell, index) => renderCell(cell, index, 'th')).join('')}</tr></thead>` +
        (normalizedRows.length
          ? `<tbody>${normalizedRows.map((row) => `<tr>${row.map((cell, index) => renderCell(cell, index, 'td')).join('')}</tr>`).join('')}</tbody>`
          : '') +
      '</table>' +
    '</div>'
  );
};

const markdownToHtml = (source) => {
  const lines = `${source ?? ''}`.replace(/\r\n?/g, '\n').split('\n');
  const html = [];
  let inUl = false;
  let inOl = false;
  let inCode = false;

  const nextContentLineIndex = (startIndex) => {
    let nextIndex = startIndex;
    while (nextIndex < lines.length && !lines[nextIndex].trim()) nextIndex += 1;
    return nextIndex;
  };

  const closeLists = () => {
    if (inUl) {
      html.push('</div>');
      inUl = false;
    }
    if (inOl) {
      html.push('</div>');
      inOl = false;
    }
  };

  for (let lineIndex = 0; lineIndex < lines.length; lineIndex += 1) {
    const rawLine = lines[lineIndex];
    const line = rawLine.trimEnd();

    if (line.startsWith('```')) {
      closeLists();
      if (!inCode) {
        inCode = true;
        html.push('<pre class="mb-3 overflow-x-auto rounded-lg bg-slate-900 p-3 text-slate-100"><code>');
      } else {
        inCode = false;
        html.push('</code></pre>');
      }
      continue;
    }

    if (inCode) {
      html.push(`${escapeHtml(rawLine)}\n`);
      continue;
    }

    if (!line) {
      closeLists();
      continue;
    }

    const separatorIndex = nextContentLineIndex(lineIndex + 1);
    const tableAlignments = separatorIndex < lines.length
      ? getMarkdownTableAlignments(lines[separatorIndex].trim())
      : null;
    const tableHeader = tableAlignments ? splitMarkdownTableRow(line) : null;
    if (tableHeader && tableHeader.length === tableAlignments.length) {
      closeLists();
      const bodyRows = [];
      lineIndex = separatorIndex + 1;
      while (lineIndex < lines.length) {
        const bodyIndex = nextContentLineIndex(lineIndex);
        if (bodyIndex >= lines.length) {
          lineIndex = lines.length;
          break;
        }
        const bodyLine = lines[bodyIndex].trim();
        if (!bodyLine.includes('|')) {
          lineIndex = bodyIndex;
          break;
        }
        const possibleNextSeparatorIndex = nextContentLineIndex(bodyIndex + 1);
        if (possibleNextSeparatorIndex < lines.length && getMarkdownTableAlignments(lines[possibleNextSeparatorIndex].trim())) {
          lineIndex = bodyIndex;
          break;
        }
        bodyRows.push(splitMarkdownTableRow(bodyLine));
        lineIndex = bodyIndex + 1;
      }
      lineIndex -= 1;
      html.push(renderMarkdownTable(tableHeader, tableAlignments, bodyRows));
      continue;
    }

    const heading = line.match(/^(#{1,6})\s+(.+)$/);
    if (heading) {
      closeLists();
      const level = heading[1].length;
      html.push(`<h${level} class="mb-2 font-bold text-[#232753]">${renderInlineMarkdown(heading[2])}</h${level}>`);
      continue;
    }

    const quote = line.match(/^>\s+(.+)$/);
    if (quote) {
      closeLists();
      html.push(`<blockquote class="mb-3 border-l-4 border-slate-300 pl-3 italic text-slate-600">${renderInlineMarkdown(quote[1])}</blockquote>`);
      continue;
    }

    if (thematicBreakPattern.test(line.trim())) {
      closeLists();
      html.push('<hr class="my-4 border-0 border-t border-slate-300" />');
      continue;
    }

    const ordered = line.match(/^(\d+)\.\s+(.+)$/);
    if (ordered) {
      if (!inOl) {
        if (inUl) {
          html.push('</div>');
          inUl = false;
        }
        html.push('<div class="mb-3" style="margin:0 0 0.75rem 0;">');
        inOl = true;
      }
      html.push(
        `<div class="mb-1" style="display:flex;align-items:flex-start;gap:0.625rem;margin:0 0 0.25rem 0;">` +
          `<span style="flex:0 0 1.6rem;min-width:1.6rem;text-align:right;">${ordered[1]}.</span>` +
          `<span style="flex:1;min-width:0;">${renderInlineMarkdown(ordered[2])}</span>` +
        `</div>`
      );
      continue;
    }

    const unordered = line.match(/^[-*]\s+(.+)$/);
    if (unordered) {
      if (!inUl) {
        if (inOl) {
          html.push('</div>');
          inOl = false;
        }
        html.push('<div class="mb-3" style="margin:0 0 0.75rem 0;">');
        inUl = true;
      }
      html.push(
        `<div class="mb-1" style="display:flex;align-items:flex-start;gap:0.625rem;margin:0 0 0.25rem 0;">` +
          `<span style="flex:0 0 1.6rem;min-width:1.6rem;text-align:center;">&bull;</span>` +
          `<span style="flex:1;min-width:0;">${renderInlineMarkdown(unordered[1])}</span>` +
        `</div>`
      );
      continue;
    }

    closeLists();
    html.push(`<p class="mb-3">${renderInlineMarkdown(line)}</p>`);
  }

  closeLists();
  if (inCode) html.push('</code></pre>');

  return html.join('');
};

const htmlToPlainText = (html) => {
  const source = `${html ?? ''}`.trim();
  if (!source) return '';

  if (typeof DOMParser !== 'undefined') {
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${source}</div>`, 'text/html');
    const root = doc.body.firstElementChild || doc.body;

    const serializeNode = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent?.replace(/\s+/g, ' ') || '';
      }
      if (node.nodeType !== Node.ELEMENT_NODE) return '';

      const tag = node.tagName.toLowerCase();
      const childText = Array.from(node.childNodes).map(serializeNode).join('');

      if (/^h[1-6]$/.test(tag)) {
        const level = Number(tag.slice(1));
        return `${'#'.repeat(level)} ${childText.trim()}\n\n`;
      }
      if (tag === 'br') return '\n';
      if (tag === 'ol') {
        return `${Array.from(node.children)
          .filter((child) => child.tagName?.toLowerCase() === 'li')
          .map((child, index) => `${index + 1}. ${Array.from(child.childNodes).map(serializeNode).join('').trim()}`)
          .join('\n')}\n\n`;
      }
      if (tag === 'ul') {
        return `${Array.from(node.children)
          .filter((child) => child.tagName?.toLowerCase() === 'li')
          .map((child) => `- ${Array.from(child.childNodes).map(serializeNode).join('').trim()}`)
          .join('\n')}\n\n`;
      }
      if (['p', 'div', 'section', 'article', 'blockquote', 'pre'].includes(tag)) {
        return `${childText.trim()}\n\n`;
      }
      if (tag === 'li') {
        return `${childText.trim()}\n`;
      }
      return childText;
    };

    return serializeNode(root)
      .replace(/\u00a0/g, ' ')
      .replace(/[ \t]+\n/g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }

  return source
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|section|article|blockquote|pre|h[1-6])>/gi, '\n\n')
    .replace(/<(p|div|section|article|blockquote|pre|h[1-6])[^>]*>/gi, '')
    .replace(/<\/li>/gi, '\n')
    .replace(/<li[^>]*>/gi, '- ')
    .replace(/<\/(ul|ol)>/gi, '\n')
    .replace(/<(ul|ol)[^>]*>/gi, '')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\u00a0/g, ' ')
    .replace(/\r\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const getTextBlockSource = (props = {}) =>
  `${props.fullContent || props.content || htmlToPlainText(props.contentHtml || '') || ''}`;

const getTextBlockPlainText = (props = {}) =>
  `${props.content || htmlToPlainText(props.contentHtml || '') || ''}`;

const getTextBlockHtml = (props = {}) => markdownToHtml(getTextBlockPlainText(props));

const youtubeIdFromUrl = (value = '') => {
  const source = `${value || ''}`.trim();
  if (!source) return '';
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/)([A-Za-z0-9_-]{6,})/,
    /youtube\.com\/embed\/([A-Za-z0-9_-]{6,})/,
    /youtube\.com\/shorts\/([A-Za-z0-9_-]{6,})/
  ];
  for (const pattern of patterns) {
    const match = source.match(pattern);
    if (match?.[1]) return match[1];
  }
  return '';
};

const youtubeWatchUrl = (id = '') => `https://www.youtube.com/watch?v=${id}`;
const youtubeThumbnailUrl = (id = '') => `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
const youtubeThumbnailBytesCache = new Map();

const renderRichTextHtml = (text = '') => {
  const blocks = `${text || ''}`.replace(/\r\n/g, '\n').split(/\n\s*\n/).map((block) => block.trim()).filter(Boolean);
  if (blocks.length === 0) return '<p class="text-slate-400">Add rich content...</p>';

  return blocks.map((block) => {
    const youtubeId = youtubeIdFromUrl(block);
    if (youtubeId && /^https?:\/\/\S+$/.test(block)) {
      return `<div class="my-3 aspect-video overflow-hidden rounded-xl border border-slate-200 bg-slate-100" data-youtube-export="true" data-youtube-url="${youtubeWatchUrl(youtubeId)}" data-youtube-id="${youtubeId}"><iframe src="https://www.youtube.com/embed/${youtubeId}" title="YouTube video" class="h-full w-full" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe></div>`;
    }

    const html = markdownToHtml(block)
      .replace(/(?<!["'=])(https?:\/\/[^\s<]+)/g, (url) => {
        const videoId = youtubeIdFromUrl(url);
        if (videoId) {
          return `<a href="${url}" target="_blank" rel="noreferrer" class="font-semibold text-[#4f46e5] underline">YouTube video</a>`;
        }
        return `<a href="${url}" target="_blank" rel="noreferrer" class="font-semibold text-[#4f46e5] underline">${url}</a>`;
      });
    return html;
  }).join('');
};

const getRichContentHtml = (props = {}) => {
  const html = `${props.contentHtml || ''}`.trim();
  if (html && !/<[a-z][\s\S]*>/i.test(html)) return renderRichTextHtml(html);
  if (html) return props.contentHtml;
  if (props.suppressEmptyContent) return '';
  return renderRichTextHtml(props.content || '');
};

const getRichMediaItems = (props = {}) => Array.isArray(props.media) ? props.media : [];

const getRichMediaHeight = (item = {}) => {
  const width = Number(item.width) || 640;
  const height = Number(item.height) || (item.type === 'video' ? Math.round(width * 9 / 16) : 220);
  return Math.max(90, Math.min(720, height));
};

const getComponentRenderWidthPx = (width = 'full') => {
  const span = getWidthSpan(width);
  const columnWidth = (PAGE_INNER_WIDTH_PX - (GRID_GAP_PX * 11)) / 12;
  return Math.round((columnWidth * span) + (GRID_GAP_PX * Math.max(0, span - 1)));
};

const createRichContentProbe = (html, props = {}) => {
  const probe = document.createElement('div');
  probe.className = `rich-content-block ${getTextSizeClass(props.textSize)} leading-6 text-slate-700`;
  probe.style.cssText = [
    'position:absolute',
    'left:-9999px',
    'top:0',
    'visibility:hidden',
    `width:${getComponentRenderWidthPx(props.width || 'full')}px`,
    'box-sizing:border-box',
    'overflow-wrap:anywhere',
    'word-break:break-word'
  ].join(';');
  probe.innerHTML = html;
  document.body.appendChild(probe);
  return probe;
};

const measureRichContentHtmlHeight = (html, props = {}) => {
  const source = `${html || ''}`;
  if (!source.trim()) return 0;
  if (typeof document === 'undefined') {
    return estimateTextHeight(htmlToPlainText(source), props.width || 'full', props.textSize || 'base', 24);
  }
  const probe = createRichContentProbe(source, props);
  try {
    return Math.ceil(probe.getBoundingClientRect().height);
  } finally {
    document.body.removeChild(probe);
  }
};

const measureRichContentHeight = (props = {}) => {
  const html = getRichContentHtml(props);
  const textHeight = props.suppressEmptyContent && !html.trim()
    ? 0
    : Math.max(72, measureRichContentHtmlHeight(html, props));
  return textHeight + getRichMediaItems(props)
    .reduce((total, item) => total + getRichMediaHeight(item) + 24, 0);
};

const getRichTextBoundary = (root, targetOffset) => {
  const walker = document.createTreeWalker(root, window.NodeFilter.SHOW_TEXT);
  let consumed = 0;
  let node = walker.nextNode();
  while (node) {
    const next = consumed + node.nodeValue.length;
    if (targetOffset <= next) {
      return { node, offset: Math.max(0, targetOffset - consumed) };
    }
    consumed = next;
    node = walker.nextNode();
  }
  return { node: root, offset: root.childNodes.length };
};

const splitRichHtmlAtTextOffset = (html, offset) => {
  const source = document.createElement('div');
  source.innerHTML = html;
  const boundary = getRichTextBoundary(source, offset);
  const firstRange = document.createRange();
  firstRange.selectNodeContents(source);
  firstRange.setEnd(boundary.node, boundary.offset);
  const restRange = document.createRange();
  restRange.selectNodeContents(source);
  restRange.setStart(boundary.node, boundary.offset);

  const serialize = (fragment) => {
    const wrapper = document.createElement('div');
    wrapper.appendChild(fragment);
    return wrapper.innerHTML;
  };
  return {
    firstHtml: serialize(firstRange.cloneContents()),
    restHtml: serialize(restRange.cloneContents())
  };
};

const splitRichHtmlToFitHeight = (html, availableHeight, props = {}) => {
  const source = `${html || ''}`;
  if (!source.trim()) return { firstHtml: '', restHtml: '' };
  if (measureRichContentHtmlHeight(source, props) <= availableHeight) {
    return { firstHtml: source, restHtml: '' };
  }
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return { firstHtml: '', restHtml: source };
  }

  const holder = document.createElement('div');
  holder.innerHTML = source;
  const textLength = holder.textContent?.length || 0;
  if (textLength < 2) return { firstHtml: '', restHtml: source };

  let low = 1;
  let high = textLength - 1;
  let best = null;
  while (low <= high) {
    const midpoint = Math.floor((low + high) / 2);
    const candidate = splitRichHtmlAtTextOffset(source, midpoint);
    const candidateHeight = Math.max(72, measureRichContentHtmlHeight(candidate.firstHtml, props));
    if (candidate.firstHtml.trim() && candidate.restHtml.trim() && candidateHeight <= availableHeight) {
      best = candidate;
      low = midpoint + 1;
    } else {
      high = midpoint - 1;
    }
  }
  return best || { firstHtml: '', restHtml: source };
};

const parseTextFlowBlocks = (text) => {
  const normalized = `${text || ''}`.replace(/\r\n/g, '\n');
  if (!normalized.trim()) return [];

  return normalized
    .split(/\n\s*\n/)
    .map((chunk) => chunk.trim())
    .filter(Boolean)
    .map((chunk) => {
      const lines = chunk.split('\n').map((line) => line.trim()).filter(Boolean);
      if (lines.length && lines.every((line) => orderedListPattern.test(line))) {
        const firstMatch = lines[0].match(orderedListPattern);
        const startNumber = firstMatch ? Number(firstMatch[1]) : 1;
        return {
          type: 'orderedList',
          items: lines.map((line, itemIndex) => {
            const match = line.match(orderedListPattern);
            return { number: startNumber + itemIndex, text: match[2] };
          })
        };
      }
      if (lines.length && lines.every((line) => unorderedListPattern.test(line))) {
        return {
          type: 'unorderedList',
          items: lines.map((line) => {
            const match = line.match(unorderedListPattern);
            return { text: match[1] };
          })
        };
      }
      if (lines.length === 1 && thematicBreakPattern.test(lines[0])) {
        return { type: 'divider' };
      }
      return { type: 'text', text: chunk };
    });
};

const textFlowBlockToText = (block) => {
  if (!block) return '';
  if (block.type === 'orderedList') {
    return block.items.map((item) => `${item.number}. ${item.text}`).join('\n');
  }
  if (block.type === 'unorderedList') {
    return block.items.map((item) => `- ${item.text}`).join('\n');
  }
  if (block.type === 'divider') {
    return '---';
  }
  return block.text || '';
};

const joinTextFlowBlocks = (blocks) =>
  blocks
    .map((block) => textFlowBlockToText(block))
    .filter(Boolean)
    .join('\n\n')
    .trim();

const splitInlineTextByRenderedLines = (text, maxLines, width = 'full', fontPx = 14) => {
  const normalized = `${text || ''}`.replace(/\r\n/g, '\n');
  if (!normalized) return { firstText: '', restText: '' };

  const charsPerLine = charsPerLineForWidth(width, fontPx);
  let remainingLines = Math.max(1, maxLines);
  let firstText = '';
  let cursor = 0;

  while (cursor < normalized.length && remainingLines > 0) {
    const newlineIndex = normalized.indexOf('\n', cursor);
    const segmentEnd = newlineIndex === -1 ? normalized.length : newlineIndex;
    const rawSegment = normalized.slice(cursor, segmentEnd);
    const renderedSegment = stripMarkdown(rawSegment);
    const segmentLines = Math.max(1, Math.ceil((renderedSegment.length || 0) / charsPerLine));

    if (segmentLines <= remainingLines) {
      firstText += rawSegment;
      remainingLines -= segmentLines;

      if (newlineIndex !== -1) {
        firstText += '\n';
        cursor = newlineIndex + 1;
      } else {
        cursor = normalized.length;
      }
      continue;
    }

    const maxChars = Math.max(charsPerLine, remainingLines * charsPerLine);
    let splitAt = rawSegment.lastIndexOf(' ', maxChars);
    if (splitAt < maxChars * 0.45) splitAt = maxChars;
    firstText += rawSegment.slice(0, splitAt);
    cursor += splitAt;
    break;
  }

  return {
    firstText: firstText.trimEnd(),
    restText: normalized.slice(cursor).trimStart()
  };
};

const splitTextByRenderedLines = (text, maxLines, width = 'full', fontPx = 14) => {
  const blocks = parseTextFlowBlocks(text);
  if (!blocks.length) return { firstText: '', restText: '' };

  const accepted = [];
  let usedLines = 0;

  for (let index = 0; index < blocks.length; index += 1) {
    const block = blocks[index];
    const blockText = textFlowBlockToText(block);
    const blockLines = estimateRenderedLineCount(blockText, width, fontPx);
    const nextBlock = blocks[index + 1] || null;
    const nextBlockLines = nextBlock
      ? estimateRenderedLineCount(textFlowBlockToText(nextBlock), width, fontPx)
      : 0;

    if (block.type === 'divider' && nextBlock && accepted.length > 0) {
      if (usedLines + blockLines + nextBlockLines > maxLines) {
        return {
          firstText: joinTextFlowBlocks(accepted),
          restText: joinTextFlowBlocks(blocks.slice(index))
        };
      }
    }

    if (usedLines + blockLines <= maxLines) {
      accepted.push(block);
      usedLines += blockLines;
      continue;
    }

    if (!accepted.length) {
      if (block.type === 'orderedList' || block.type === 'unorderedList') {
        const firstItems = [];
        let itemLines = 0;
        for (let itemIndex = 0; itemIndex < block.items.length; itemIndex += 1) {
          const item = block.items[itemIndex];
          const itemText = block.type === 'orderedList' ? `${item.number}. ${item.text}` : `- ${item.text}`;
          const currentLines = estimateRenderedLineCount(itemText, width, fontPx);
          if (itemLines + currentLines <= maxLines) {
            firstItems.push(item);
            itemLines += currentLines;
            continue;
          }

          if (!firstItems.length) {
            const prefix = block.type === 'orderedList' ? `${item.number}. ` : '- ';
            const split = splitInlineTextByRenderedLines(`${prefix}${item.text}`, maxLines, width, fontPx);
            const stripPrefix = (value) => (
              value.startsWith(prefix)
                ? value.slice(prefix.length).trimStart()
                : value.trimStart()
            );
            const firstPartText = stripPrefix(split.firstText);
            const restPartText = stripPrefix(split.restText);

            if (firstPartText) {
              firstItems.push(
                block.type === 'orderedList'
                  ? { number: item.number, text: firstPartText }
                  : { text: firstPartText }
              );
            }

            const restItems = [];
            if (restPartText) {
              restItems.push(
                block.type === 'orderedList'
                  ? { number: item.number, text: restPartText }
                  : { text: restPartText }
              );
            }
            restItems.push(...block.items.slice(itemIndex + 1));
            return {
              firstText: joinTextFlowBlocks([{ ...block, items: firstItems }]),
              restText: joinTextFlowBlocks([{ ...block, items: restItems }, ...blocks.slice(index + 1)])
            };
          }
          return {
            firstText: joinTextFlowBlocks([{ ...block, items: firstItems }]),
            restText: joinTextFlowBlocks([{ ...block, items: block.items.slice(itemIndex) }, ...blocks.slice(index + 1)])
          };
        }
      } else {
        const split = splitInlineTextByRenderedLines(blockText, maxLines, width, fontPx);
        return {
          firstText: split.firstText,
          restText: joinTextFlowBlocks([{ type: 'text', text: split.restText }, ...blocks.slice(index + 1)])
        };
      }
    }

    return {
      firstText: joinTextFlowBlocks(accepted),
      restText: joinTextFlowBlocks(blocks.slice(index))
    };
  }

  return {
    firstText: joinTextFlowBlocks(accepted),
    restText: ''
  };
};

const parseEmailList = (raw = '') => {
  const normalized = raw
    .split(',')
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean)
    .filter((item, index, all) => all.indexOf(item) === index);
  return normalized.filter((email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
};

const evalRule = (rule, value) => {
  const source = `${value ?? ''}`;
  const target = `${rule.value ?? ''}`;

  switch (rule.operator) {
    case 'equals':
      return source === target;
    case 'notEquals':
      return source !== target;
    case 'contains':
      return source.toLowerCase().includes(target.toLowerCase());
    case 'notContains':
      return !source.toLowerCase().includes(target.toLowerCase());
    case 'greaterThan':
      return Number(source) > Number(target);
    case 'lessThan':
      return Number(source) < Number(target);
    case 'isEmpty':
      return source.trim() === '';
    case 'isNotEmpty':
      return source.trim() !== '';
    default:
      return false;
  }
};

const isFillableSmartType = (type) =>
  ['textInput', 'textareaInput', 'dropdown', 'radio', 'checklist', 'checkbox', 'datePicker', 'barcodeInput', 'qrCodeInput', 'nfcInput', 'rating', 'progress', 'trafficLightStatus'].includes(type);

const flattenSmartComponents = (items = []) =>
  items.flatMap((component) => (component?.type === 'componentGroup' ? flattenSmartComponents(component.children || []) : [component]));

const isSmartChipFilled = (data = {}) =>
  Boolean(`${data?.text || ''}`.trim()) ||
  Number(data?.rating || 0) > 0 ||
  Number(data?.progress || 0) > 0 ||
  ['red', 'orange', 'green'].includes(data?.status);

const formatSubmissionDate = (value, fallbackIso = '') => {
  const candidate = value?.toDate?.() || (fallbackIso ? new Date(fallbackIso) : null);
  if (!(candidate instanceof Date) || Number.isNaN(candidate.getTime())) return 'Unknown time';
  return candidate.toLocaleString();
};

const addPdfLinkAnnotation = (pdfDoc, page, x, y, width, height, url) => {
  const annotation = pdfDoc.context.obj({
    Type: 'Annot',
    Subtype: 'Link',
    Rect: [x, y, x + width, y + height],
    Border: [0, 0, 0],
    A: {
      Type: 'Action',
      S: 'URI',
      URI: PDFString.of(url)
    }
  });
  const annotationRef = pdfDoc.context.register(annotation);
  const annots = page.node.lookupMaybe(PDFName.of('Annots'), PDFArray);
  if (annots) {
    annots.push(annotationRef);
  } else {
    page.node.set(PDFName.of('Annots'), pdfDoc.context.obj([annotationRef]));
  }
};

const getYoutubeExportTargets = (node) => {
  if (!node) return [];
  const rootRect = node.getBoundingClientRect();
  const elements = Array.from(node.querySelectorAll('[data-youtube-export="true"], iframe[src*="youtube.com/embed"]'));
  const seen = new Set();
  return elements
    .map((el) => {
      const targetEl = el.closest?.('[data-youtube-export="true"]') || el;
      if (seen.has(targetEl)) return null;
      seen.add(targetEl);
      const rect = targetEl.getBoundingClientRect();
      const fallbackId = youtubeIdFromUrl(el.getAttribute('src') || '');
      const url = targetEl.getAttribute?.('data-youtube-url') || (fallbackId ? youtubeWatchUrl(fallbackId) : '');
      if (!url || rect.width <= 0 || rect.height <= 0) return null;
      return {
        url,
        id: targetEl.getAttribute?.('data-youtube-id') || fallbackId,
        left: rect.left - rootRect.left,
        top: rect.top - rootRect.top,
        width: rect.width,
        height: rect.height
      };
    })
    .filter(Boolean);
};

const replaceYoutubeEmbedsForExport = (clonedDoc) => {
  const replaceElement = (el, source = el) => {
    const id = source.getAttribute('data-youtube-id') || youtubeIdFromUrl(source.getAttribute('data-youtube-url') || source.getAttribute('src') || '');
    const url = source.getAttribute('data-youtube-url') || (id ? youtubeWatchUrl(id) : '');
    if (!id || !url) return;
    const thumbnail = id ? youtubeThumbnailUrl(id) : '';
    const replacement = clonedDoc.createElement('a');
    replacement.href = url;
    replacement.setAttribute('data-youtube-export', 'true');
    replacement.setAttribute('data-youtube-url', url);
    replacement.style.cssText = [
      'position:relative',
      'display:block',
      'width:100%',
      'height:100%',
      'min-height:180px',
      'overflow:hidden',
      'border:1px solid #cbd5e1',
      'border-radius:12px',
      'background:#0f172a',
      'text-decoration:none'
    ].join(';');

    if (thumbnail) {
      const img = clonedDoc.createElement('img');
      img.src = thumbnail;
      img.setAttribute('referrerpolicy', 'no-referrer');
      img.style.cssText = 'display:block;position:absolute;inset:0;width:100%;height:100%;object-fit:cover;';
      replacement.appendChild(img);
    }

    const overlay = clonedDoc.createElement('div');
    overlay.style.cssText = 'position:absolute;inset:0;display:flex;align-items:center;justify-content:center;background:rgba(15,23,42,0.18);';
    overlay.innerHTML = '<div style="display:flex;height:56px;width:56px;align-items:center;justify-content:center;border-radius:999px;background:rgba(79,70,229,0.92);color:white;font-size:26px;font-weight:700;box-shadow:0 12px 30px rgba(15,23,42,0.28);">▶</div>';
    replacement.appendChild(overlay);

    el.replaceChildren(replacement);
  };

  clonedDoc.querySelectorAll('[data-youtube-export="true"]').forEach((el) => {
    replaceElement(el);
  });

  clonedDoc.querySelectorAll('iframe[src*="youtube.com/embed"]').forEach((iframe) => {
    if (iframe.closest('[data-youtube-export="true"]')) return;
    const wrapper = clonedDoc.createElement('div');
    wrapper.style.cssText = 'width:100%;height:100%;min-height:180px;';
    iframe.replaceWith(wrapper);
    replaceElement(wrapper, iframe);
  });
};

const embedYoutubeThumbnail = async (pdfDoc, youtubeId) => {
  if (!youtubeId) return null;
  try {
    let bytes = youtubeThumbnailBytesCache.get(youtubeId);
    if (!bytes) {
      const response = await fetch(youtubeThumbnailUrl(youtubeId), { mode: 'cors' });
      if (!response.ok) return null;
      bytes = await response.arrayBuffer();
      youtubeThumbnailBytesCache.set(youtubeId, bytes);
    }
    return await pdfDoc.embedJpg(bytes);
  } catch (error) {
    console.warn('Failed to embed YouTube thumbnail in PDF:', error);
    return null;
  }
};

const SmartChipModal = ({ config, initialData, onSave, onClose }) => {
  const [text, setText] = useState(initialData?.text || '');
  const [rating, setRating] = useState(Number(initialData?.rating || 0));
  const [progressValue, setProgressValue] = useState(Number(initialData?.progress || 0));
  const [status, setStatus] = useState(['red', 'orange', 'green'].includes(initialData?.status) ? initialData.status : 'orange');

  return (
    <div
      className="absolute right-0 top-full z-50 mt-3 w-80 rounded-xl border border-slate-200 bg-white shadow-2xl"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
        <h4 className="text-sm font-bold text-[#232753]">Smart Chip</h4>
        <button onClick={onClose} className="text-xs font-semibold text-slate-500 hover:text-[#232753]">Close</button>
      </div>
      <div className="space-y-4 p-4">
        {config?.enableText !== false && (
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Notes</label>
            <textarea
              rows={3}
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
            />
          </div>
        )}
        {config?.enableRating && (
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Rating</label>
            <div className="flex items-center gap-1">
              {Array.from({ length: 5 }).map((_, index) => {
                const active = index < rating;
                return (
                  <button
                    key={`viewer-smart-chip-rating-${index}`}
                    type="button"
                    onClick={() => setRating(index + 1)}
                    className={`text-[2.5rem] leading-none ${active ? 'text-[#fbbf24]' : 'text-slate-300'}`}
                  >
                    *
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {config?.enableProgress && (
          <div>
            <div className="mb-1 flex items-center justify-between">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Progress</label>
              <span className="text-xs font-semibold text-[#232753]">{progressValue}%</span>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              value={progressValue}
              onChange={(e) => setProgressValue(Number(e.target.value))}
              className="w-full accent-[#4f46e5]"
            />
          </div>
        )}
        {config?.enableStatus && (
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Status</label>
            <div className="flex items-center gap-2">
              {[
                { value: 'red', label: 'R', activeClass: 'bg-rose-500 text-white border-rose-500', idleClass: 'border-rose-200 bg-rose-50 text-rose-500' },
                { value: 'orange', label: 'O', activeClass: 'bg-amber-500 text-white border-amber-500', idleClass: 'border-amber-200 bg-amber-50 text-amber-500' },
                { value: 'green', label: 'G', activeClass: 'bg-emerald-500 text-white border-emerald-500', idleClass: 'border-emerald-200 bg-emerald-50 text-emerald-500' }
              ].map((option) => {
                const active = status === option.value;
                return (
                  <button
                    key={`viewer-smart-chip-status-${option.value}`}
                    type="button"
                    onClick={() => setStatus(option.value)}
                    className={`flex h-10 w-10 items-center justify-center rounded-full border text-sm font-bold ${
                      active ? option.activeClass : option.idleClass
                    }`}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
      <div className="flex justify-end gap-2 border-t border-slate-100 px-4 py-3">
        <button onClick={onClose} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600">Cancel</button>
        <button
          onClick={() => onSave({ text, rating, progress: progressValue, status })}
          className="rounded-lg bg-[#232753] px-3 py-1.5 text-xs font-semibold text-white"
        >
          Save
        </button>
      </div>
    </div>
  );
};

const SmartDocComponent = ({ component, fieldKey, value, onValueChange, onSubmit, submitting = false, readOnly = false }) => {
  const props = component.props || {};
  const alignClass = props.alignment === 'center' ? 'text-center' : props.alignment === 'right' ? 'text-right' : 'text-left';
  const justifyClass = props.alignment === 'center' ? 'justify-center' : props.alignment === 'right' ? 'justify-end' : 'justify-start';
  const textSizeClass = getTextSizeClass(props.textSize);

  if (component.type === 'heading') {
    const level = props.level || 2;
    const levelSize = level === 1 ? 'text-3xl' : level === 2 ? 'text-2xl' : 'text-xl';
    const size = props.textSize ? textSizeClass : levelSize;
    return <h3 className={`mb-3 font-bold text-[#232753] ${size} ${alignClass}`}>{props.content}</h3>;
  }

  if (component.type === 'textBlock') {
    const htmlValue = getTextBlockHtml(props);
    return (
      <div
        className={`mb-3 leading-6 text-slate-700 ${textSizeClass} ${alignClass}`}
        dangerouslySetInnerHTML={{ __html: htmlValue || '' }}
      />
    );
  }

  if (component.type === 'richContent') {
    const media = getRichMediaItems(props);
    const htmlValue = getRichContentHtml(props);
    return (
      <div className={`rich-content-block leading-6 text-slate-700 ${textSizeClass} ${alignClass}`}>
        {htmlValue.trim() && <div dangerouslySetInnerHTML={{ __html: htmlValue }} />}
        {media.map((item, index) => {
          const width = Math.max(120, Math.min(760, Number(item.width) || 640));
          const height = getRichMediaHeight(item);
          const mediaAlignClass = item.alignment === 'center' ? 'mx-auto' : item.alignment === 'right' ? 'ml-auto' : 'mr-auto';
          return (
            <div
              key={item.id || `${component.id}-rich-media-${index}`}
              className={`my-3 max-w-full ${mediaAlignClass}`}
              style={{ width: `${width}px` }}
            >
              {item.type === 'video' ? (
                <video
                  src={item.src}
                  controls
                  className="block w-full rounded-xl border border-slate-200 bg-black"
                  style={{ height: `${height}px` }}
                />
              ) : (
                <img
                  src={item.src}
                  alt={item.alt || 'Rich content media'}
                  className="block w-full rounded-xl border border-slate-200 object-contain"
                  style={{ height: `${height}px` }}
                  loading="eager"
                  draggable={false}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  }

  if (component.type === 'sectionDivider') {
    const lineStyleMap = {
      solid: 'solid',
      dashed: 'dashed',
      dotted: 'dotted'
    };
    const showLabel = props.showLabel !== false && props.content;
    const thickness = Math.max(1, Math.min(6, Number(props.lineThickness ?? 1)));
    const spacingTop = Math.max(0, Math.min(80, Number(props.spacingTop ?? 24)));
    const spacingBottom = Math.max(0, Math.min(80, Number(props.spacingBottom ?? 24)));
    const labelClass = props.alignment === 'center' ? 'mx-auto' : props.alignment === 'right' ? 'ml-auto' : 'mr-auto';

    return (
      <div className="w-full" style={{ paddingTop: `${spacingTop}px`, paddingBottom: `${spacingBottom}px` }}>
        {showLabel && (
          <div className={`mb-3 w-fit rounded-full bg-white px-3 py-1 font-semibold uppercase tracking-[0.22em] text-slate-500 shadow-sm ${labelClass} ${textSizeClass}`}>
            {props.content}
          </div>
        )}
        <div
          className="w-full"
          style={{
            borderTopWidth: `${thickness}px`,
            borderTopStyle: lineStyleMap[props.lineStyle] || 'solid',
            borderTopColor: props.lineColor || '#cbd5e1'
          }}
        />
      </div>
    );
  }

  if (component.type === 'navigationMenu') {
    const items = Array.isArray(props.items) ? props.items : [];
    return (
      <nav className="mb-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
        <div className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-[0.18em] text-[#232753]">
          <ListTree className="h-4 w-4 text-[#4f46e5]" />
          <span>{props.title || 'Document Navigation'}</span>
        </div>
        {items.length === 0 ? (
          <p className="text-sm text-slate-500">No document sections are available.</p>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2">
            {items.map((item) => (
              <a
                key={`${component.id}-${item.id}`}
                href={`#smart-section-${item.id}`}
                className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-[#232753] hover:border-[#4f46e5] hover:bg-[#eef2ff]"
              >
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#eef2ff] text-xs text-[#4f46e5]">
                  {item.order}
                </span>
                <span className="min-w-0 truncate">{item.label}</span>
              </a>
            ))}
          </div>
        )}
      </nav>
    );
  }

  if (component.type === 'pageBreak') {
    return null;
  }

  if (component.type === 'image') {
    const width = Number(props.imgWidth) || 720;
    const height = Number(props.imgHeight) || undefined;
    const cropScale = Math.max(1, Math.min(3, Number(props.cropScale) || 1));
    const cropX = Math.max(0, Math.min(100, Number(props.cropX ?? 50)));
    const cropY = Math.max(0, Math.min(100, Number(props.cropY ?? 50)));
    const cropEnabled = Boolean(height);
    return (
      <div className={`mb-0 flex ${justifyClass}`}>
        <div className="relative inline-block max-w-full" style={{ width: `${Math.min(width, 760)}px`, maxWidth: '100%' }}>
          <div className="relative w-full overflow-hidden" style={{ height: height ? `${height}px` : 'auto' }}>
            <img
              src={props.src}
              alt={props.alt || 'Document image'}
              referrerPolicy="no-referrer"
              crossOrigin="anonymous"
              loading="eager"
              draggable={false}
              className={`block w-full select-none ${cropEnabled ? 'h-full max-w-none object-cover' : 'h-auto object-contain'}`}
              style={
                cropEnabled
                  ? {
                      transform: `scale(${cropScale})`,
                      transformOrigin: `${cropX}% ${cropY}%`
                    }
                  : { height: 'auto' }
              }
            />
          </div>
        </div>
      </div>
    );
  }

  if (component.type === 'textInput') {
    return (
      <label className="mb-3 block">
        <span className={`mb-1 block font-semibold text-[#232753] ${alignClass} ${textSizeClass}`}>{props.label}</span>
        <input
          data-field-key={fieldKey}
          name={fieldKey}
          type="text"
          value={value || ''}
          placeholder={props.placeholder || ''}
          readOnly={readOnly}
          onChange={(e) => onValueChange(e.target.value)}
          className={`w-full rounded-xl border border-slate-300 px-3 py-2 focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/30 ${textSizeClass}`}
        />
      </label>
    );
  }

  if (component.type === 'textareaInput') {
    return (
      <label className="mb-3 block">
        <span className={`mb-1 block font-semibold text-[#232753] ${alignClass} ${textSizeClass}`}>{props.label}</span>
        <textarea
          data-field-key={fieldKey}
          name={fieldKey}
          value={value || ''}
          rows={props.rows || 3}
          placeholder={props.placeholder || ''}
          readOnly={readOnly}
          onChange={(e) => onValueChange(e.target.value)}
          className={`w-full rounded-xl border border-slate-300 px-3 py-2 focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/30 ${textSizeClass}`}
        />
      </label>
    );
  }

  if (component.type === 'dropdown') {
    return (
      <label className="mb-3 block">
        <span className={`mb-1 block font-semibold text-[#232753] ${alignClass} ${textSizeClass}`}>{props.label}</span>
        <select
          data-field-key={fieldKey}
          name={fieldKey}
          value={value || ''}
          disabled={readOnly}
          onChange={(e) => onValueChange(e.target.value)}
          className={`w-full rounded-xl border border-slate-300 px-3 py-2 focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/30 ${textSizeClass}`}
        >
          <option value="">Select an option</option>
          {(props.options || []).map((opt) => (
            <option key={`${component.id}-${opt}`} value={opt}>{opt}</option>
          ))}
        </select>
      </label>
    );
  }

  if (component.type === 'checkbox') {
    return (
      <label className={`mb-3 flex items-center gap-2 font-medium text-[#232753] ${textSizeClass} ${alignClass}`}>
        <input
          data-field-key={fieldKey}
          name={fieldKey}
          type="checkbox"
          checked={Boolean(value)}
          disabled={readOnly}
          onChange={(e) => onValueChange(e.target.checked)}
          className="h-4 w-4 rounded border-slate-400 text-[#4f46e5] focus:ring-[#4f46e5]"
        />
        <span>{props.label}</span>
      </label>
    );
  }

  if (component.type === 'radio') {
    return (
      <fieldset className="mb-3">
        <legend className={`mb-1 block font-semibold text-[#232753] ${alignClass} ${textSizeClass}`}>{props.label}</legend>
        <div className="space-y-1">
          {(props.options || []).map((opt) => (
            <label key={`${component.id}-radio-${opt}`} className={`flex items-center gap-2 text-[#232753] ${textSizeClass}`}>
              <input
                data-field-key={fieldKey}
                name={fieldKey}
                type="radio"
                value={opt}
                checked={(value || '') === opt}
                disabled={readOnly}
                onChange={() => onValueChange(opt)}
                className="h-4 w-4 border-slate-400 text-[#4f46e5] focus:ring-[#4f46e5]"
              />
              <span>{opt}</span>
            </label>
          ))}
        </div>
      </fieldset>
    );
  }

  if (component.type === 'checklist') {
    const selectedValues = Array.isArray(value) ? value : [];
    const toggleChecklistValue = (option) => {
      if (selectedValues.includes(option)) {
        onValueChange(selectedValues.filter((item) => item !== option));
      } else {
        onValueChange([...selectedValues, option]);
      }
    };

    return (
      <fieldset className="mb-3">
        <legend className={`mb-1 block font-semibold text-[#232753] ${alignClass} ${textSizeClass}`}>{props.label}</legend>
        <div className="space-y-1">
          {(props.options || []).map((opt) => (
            <label key={`${component.id}-checklist-${opt}`} className={`flex items-center gap-2 text-[#232753] ${textSizeClass}`}>
              <input
                data-field-key={fieldKey}
                data-checklist-option={opt}
                type="checkbox"
                checked={selectedValues.includes(opt)}
                disabled={readOnly}
                onChange={() => toggleChecklistValue(opt)}
                className="h-4 w-4 rounded border-slate-400 text-[#4f46e5] focus:ring-[#4f46e5]"
              />
              <span>{opt}</span>
            </label>
          ))}
        </div>
      </fieldset>
    );
  }

  if (component.type === 'datePicker') {
    return (
      <label className="mb-3 block">
        <span className={`mb-1 block font-semibold text-[#232753] ${alignClass} ${textSizeClass}`}>{props.label}</span>
        <input
          data-field-key={fieldKey}
          name={fieldKey}
          type="date"
          value={value || ''}
          readOnly={readOnly}
          onChange={(e) => onValueChange(e.target.value)}
          className={`w-full rounded-xl border border-slate-300 px-3 py-2 focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/30 ${textSizeClass}`}
        />
      </label>
    );
  }

  if (['barcodeInput', 'qrCodeInput', 'nfcInput'].includes(component.type)) {
    return (
      <SmartSensorField
        type={component.type}
        fieldKey={fieldKey}
        value={value || ''}
        label={props.label}
        placeholder={props.placeholder || ''}
        textSizeClass={textSizeClass}
        alignClass={alignClass}
        onValueChange={onValueChange}
        readOnly={readOnly}
      />
    );
  }

  if (component.type === 'progress') {
    const progressValue = Math.max(0, Math.min(100, Number(value ?? props.progressValue ?? 0)));
    const progressColor = props.progressColor || '#4f46e5';
    return (
      <div className="mb-3">
        <div className="mb-1 flex items-center justify-between">
          <span className={`font-semibold text-[#232753] ${textSizeClass}`}>{props.label || 'Progress'}</span>
          <span className="text-xs font-semibold text-slate-500">{progressValue}%</span>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200">
          <div className="h-full rounded-full transition-all" style={{ width: `${progressValue}%`, backgroundColor: progressColor }} />
        </div>
      </div>
    );
  }

  if (component.type === 'rating') {
    const max = Math.max(1, Math.min(10, Number(props.maxRating ?? 5)));
    const current = Math.max(0, Math.min(max, Number(value ?? 0)));
    return (
      <div className="mb-3">
        <div className={`mb-1 font-semibold text-[#232753] ${textSizeClass}`}>{props.label || 'Rating'}</div>
        <div className="flex items-center gap-1">
          {Array.from({ length: max }).map((_, idx) => {
            const active = idx < current;
            return (
              <button
                key={`${component.id}-star-${idx}`}
                type="button"
                disabled={readOnly}
                onClick={() => onValueChange(idx + 1)}
                className={`text-[2.5rem] leading-none ${active ? 'text-[#fbbf24]' : 'text-slate-300'} ${readOnly ? 'cursor-default' : ''}`}
              >
                *
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (component.type === 'trafficLightStatus') {
    const current = ['red', 'orange', 'green'].includes(value) ? value : (['red', 'orange', 'green'].includes(props.value) ? props.value : 'orange');
    return (
      <div className="mb-3">
        <div className={`mb-1.5 font-semibold text-[#232753] ${alignClass} ${textSizeClass}`}>{props.label || 'Status'}</div>
        <div className={`flex flex-wrap items-center gap-2 ${justifyClass}`}>
          {[
            { value: 'red', label: 'R', activeClass: 'bg-rose-500 text-white border-rose-500', idleClass: 'border-rose-200 bg-rose-50 text-rose-500' },
            { value: 'orange', label: 'O', activeClass: 'bg-amber-500 text-white border-amber-500', idleClass: 'border-amber-200 bg-amber-50 text-amber-500' },
            { value: 'green', label: 'G', activeClass: 'bg-emerald-500 text-white border-emerald-500', idleClass: 'border-emerald-200 bg-emerald-50 text-emerald-500' }
          ].map((status) => {
            const active = current === status.value;
            return (
              <button
                key={`${component.id}-traffic-${status.value}`}
                data-field-key={fieldKey}
                data-export-keep="true"
                type="button"
                disabled={readOnly}
                onClick={() => onValueChange(status.value)}
                className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border text-[1.05rem] font-bold leading-none transition ${
                  active ? status.activeClass : status.idleClass
                } ${readOnly ? 'cursor-default' : ''}`}
                title={status.value}
              >
                {status.label}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (component.type === 'submitButton') {
    return (
      <div data-export-hide="true" className={`flex pt-2 ${justifyClass}`}>
        <button
          type="button"
          onClick={() => onSubmit?.(component.id)}
          disabled={submitting}
          className={`rounded-xl bg-[#232753] px-4 py-2 font-semibold text-white hover:bg-[#1b1f47] disabled:cursor-not-allowed disabled:opacity-70 ${textSizeClass}`}
        >
          {submitting ? 'Submitting...' : (props.buttonText || 'Submit')}
        </button>
      </div>
    );
  }

  return null;
};

const FormViewer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [form, setForm] = useState(null);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareVisibility, setShareVisibility] = useState('private');
  const [shareEmailsInput, setShareEmailsInput] = useState('');
  const [shareTitle, setShareTitle] = useState('');
  const [shareDescription, setShareDescription] = useState('');
  const [isSavingShare, setIsSavingShare] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeSmartChipId, setActiveSmartChipId] = useState(null);
  const [smartChipValues, setSmartChipValues] = useState({});
  const [ownerSubmissions, setOwnerSubmissions] = useState([]);
  const [submissionsLoading, setSubmissionsLoading] = useState(false);
  const [submissionsError, setSubmissionsError] = useState(null);
  const [submissionsRefreshTick, setSubmissionsRefreshTick] = useState(0);

  const valuesRef = useRef(values);
  const formRef = useRef(null);
  const smartExportRef = useRef(null);

  useEffect(() => {
    valuesRef.current = values;
  }, [values]);

  useEffect(() => {
    const loadForm = async () => {
      setLoading(true);
      try {
        const data = await formService.getFormById(id);
        const signedInEmail = `${user?.email || ''}`.trim().toLowerCase();
        const allowedEmails = Array.isArray(data.allowedEmails)
          ? data.allowedEmails.map((email) => `${email || ''}`.trim().toLowerCase())
          : [];
        const hasEmailAccess = Boolean(signedInEmail) && allowedEmails.includes(signedInEmail);
        const canRead =
          data.visibility === 'public' ||
          data.userId === user?.uid ||
          (data.visibility === 'restricted' && hasEmailAccess);

        if (!canRead) {
          setError(data.visibility === 'restricted' ? 'Please sign in with an allowed email to access this form.' : 'This form is private.');
          setLoading(false);
          return;
        }
        setForm(data);
        setError(null);
      } catch (err) {
        setError(err?.message || 'Failed to load form.');
      } finally {
        setLoading(false);
      }
    };
    loadForm();
  }, [id, user]);

  const isOwner = form?.userId && user?.uid === form.userId;
  const isSharedView = Boolean(form) && !isOwner;
  const isHtmlForm = Boolean(form?.html);
  const htmlStyles = useMemo(() => {
    if (!form?.html) return '';
    const matches = form.html.match(/<style[^>]*>[\s\S]*?<\/style>/gi);
    return matches ? matches.join('') : '';
  }, [form?.html]);
  const htmlContent = useMemo(() => {
    if (!form?.html) return '';
    const bodyMatch = form.html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
    if (bodyMatch) return bodyMatch[1];
    return form.html;
  }, [form?.html]);
  const htmlHasInputs = useMemo(() => {
    if (!form?.html) return false;
    return /<input|<select|<textarea/i.test(form.html);
  }, [form?.html]);
  const smartComponents = useMemo(
    () => flattenSmartComponents(form?.smartDocument?.components || []),
    [form?.smartDocument?.components]
  );
  const hasSmartDocument = smartComponents.length > 0;
  const hasSmartSubmitButton = useMemo(
    () => smartComponents.some((component) => component?.type === 'submitButton'),
    [smartComponents]
  );
  const smartDocConfig = useMemo(
    () => ({
      ...DEFAULT_SMART_DOC_CONFIG,
      ...(form?.smartDocument?.documentConfig || {})
    }),
    [form?.smartDocument?.documentConfig]
  );
  const navigationItems = useMemo(
    () => buildNavigationItems(form?.smartDocument?.components || []),
    [form?.smartDocument?.components]
  );
  const smartChipComponentMap = useMemo(() => (
    smartComponents.reduce((acc, component) => {
      if (component?.props?.smartChipConfig?.enabled) {
        acc[component.id] = component;
      }
      return acc;
    }, {})
  ), [smartComponents]);

  const defaultValues = useMemo(() => {
    if (!form?.fields) return {};
    return form.fields.reduce((acc, field) => {
      if (field.type === 'checkbox') {
        acc[field.id] = false;
      } else {
        acc[field.id] = '';
      }
      return acc;
    }, {});
  }, [form]);
  const smartDefaultValues = useMemo(() => {
    if (!hasSmartDocument) return {};
    return smartComponents.reduce((acc, component) => {
      const key = component?.props?.variableName || component.id;
      if (!key) return acc;
      const type = component.type;
      const existing = component?.props?.value;

      if (existing !== undefined && existing !== null && existing !== '') {
        if (type === 'checkbox') {
          acc[key] = existing === true || existing === 'true';
        } else if (type === 'checklist') {
          acc[key] = Array.isArray(existing) ? existing : [];
        } else {
          acc[key] = existing;
        }
      } else if (type === 'checkbox') {
        acc[key] = false;
      } else if (type === 'checklist') {
        acc[key] = [];
      } else if (type === 'progress') {
        acc[key] = Number(component?.props?.progressValue ?? 0);
      } else if (type === 'rating') {
        acc[key] = Number(component?.props?.value ?? 0);
      } else if (type === 'trafficLightStatus') {
        acc[key] = component?.props?.value || 'orange';
      } else {
        acc[key] = '';
      }
      return acc;
    }, {});
  }, [hasSmartDocument, smartComponents]);

  useEffect(() => {
    if (hasSmartDocument) {
      setValues((prev) => (Object.keys(prev).length ? prev : smartDefaultValues));
      return;
    }
    if (form?.fields) {
      setValues((prev) => (Object.keys(prev).length ? prev : defaultValues));
    }
  }, [form, defaultValues, hasSmartDocument, smartDefaultValues]);

  useEffect(() => {
    if (!hasSmartDocument) {
      setSmartChipValues({});
      return;
    }
    const initial = smartComponents.reduce((acc, component) => {
      if (!component?.props?.smartChipConfig?.enabled || !component?.id) return acc;
      const existing = component?.props?.smartChipData || {};
      if (isSmartChipFilled(existing)) {
        acc[component.id] = {
          text: `${existing?.text || ''}`,
          rating: Number(existing?.rating || 0),
          progress: Number(existing?.progress || 0),
          status: ['red', 'orange', 'green'].includes(existing?.status) ? existing.status : 'orange'
        };
      }
      return acc;
    }, {});
    setSmartChipValues(initial);
    setActiveSmartChipId(null);
  }, [hasSmartDocument, smartComponents]);

  useEffect(() => {
    if (!form) return;
    const pageTitle = (form.shareSettings?.title || form.title || 'ParrotDocs Document').trim();
    const pageDescription = (
      form.shareSettings?.description ||
      form.config?.instructions ||
      'Shared document from ParrotDocs'
    ).trim();
    const pageUrl = `${window.location.origin}/form/${form.id}`;

    document.title = pageTitle;

    const upsertMeta = (selector, attr, value, content) => {
      let meta = document.head.querySelector(selector);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, value);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    upsertMeta('meta[name="description"]', 'name', 'description', pageDescription);
    upsertMeta('meta[property="og:title"]', 'property', 'og:title', pageTitle);
    upsertMeta('meta[property="og:description"]', 'property', 'og:description', pageDescription);
    upsertMeta('meta[property="og:type"]', 'property', 'og:type', 'article');
    upsertMeta('meta[property="og:url"]', 'property', 'og:url', pageUrl);
    upsertMeta('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary');
    upsertMeta('meta[name="twitter:title"]', 'name', 'twitter:title', pageTitle);
    upsertMeta('meta[name="twitter:description"]', 'name', 'twitter:description', pageDescription);
  }, [form]);

  const handleChange = (fieldId, value) => {
    setValues(prev => ({ ...prev, [fieldId]: value }));
  };

  const isSmartComponentVisible = (component) => {
    const logic = component?.props?.visibilityLogic;
    if (!logic?.rules?.length) return true;
    const checks = logic.rules.map((rule) => {
      const target = smartComponents.find((c) => (c?.props?.variableName || c.id) === rule.fieldId);
      const targetKey = target?.props?.variableName || target?.id;
      const raw = targetKey ? values[targetKey] : '';
      const compareValue = typeof raw === 'boolean' ? String(raw) : raw;
      return evalRule(rule, compareValue);
    });
    const pass = logic.condition === 'any' ? checks.some(Boolean) : checks.every(Boolean);
    return logic.action === 'hide' ? !pass : pass;
  };

  const smartPageContentHeight = useMemo(
    () => getPageContentHeight(Boolean(smartDocConfig.showHeader && !isSharedView), Boolean(smartDocConfig.showFooter)),
    [smartDocConfig.showHeader, smartDocConfig.showFooter, isSharedView]
  );

  const visibleSmartComponents = useMemo(
    () => smartComponents.filter((component) => {
      const logic = component?.props?.visibilityLogic;
      if (!logic?.rules?.length) return true;
      const checks = logic.rules.map((rule) => {
        const target = smartComponents.find((c) => (c?.props?.variableName || c.id) === rule.fieldId);
        const targetKey = target?.props?.variableName || target?.id;
        const raw = targetKey ? values[targetKey] : '';
        const compareValue = typeof raw === 'boolean' ? String(raw) : raw;
        return evalRule(rule, compareValue);
      });
      const pass = logic.condition === 'any' ? checks.some(Boolean) : checks.every(Boolean);
      return logic.action === 'hide' ? !pass : pass;
    }),
    [smartComponents, values]
  );

  const paginatedSmartPages = useMemo(
    () => paginateSmartComponents(
      visibleSmartComponents,
      smartPageContentHeight - SMART_PAGE_PADDING_TOP_PX - SMART_PAGE_PADDING_BOTTOM_PX
    ),
    [visibleSmartComponents, smartPageContentHeight]
  );

  const validateRequiredSubmission = () => {
    if (hasSmartDocument) {
      const missing = smartComponents.filter((component) => {
        if (!isSmartComponentVisible(component)) return false;
        if (!component?.props?.required) return false;
        if (!isFillableSmartType(component.type)) return false;
        const key = component?.props?.variableName || component.id;
        const raw = values[key];
        if (component.type === 'checkbox') return raw !== true;
        if (component.type === 'checklist') return !Array.isArray(raw) || raw.length === 0;
        return `${raw ?? ''}`.trim() === '';
      });
      if (missing.length > 0) {
        toast.error(`Complete required fields before submit (${missing.length} missing).`);
        return false;
      }
      return true;
    }

    if (isHtmlForm) return true;

    const missing = (form?.fields || []).filter((field) => {
      if (!field?.required) return false;
      const raw = values[field.id];
      if (field.type === 'checkbox') return raw !== true;
      return `${raw ?? ''}`.trim() === '';
    });
    if (missing.length > 0) {
      toast.error(`Complete required fields before submit (${missing.length} missing).`);
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (!formRef.current || !isHtmlForm) return;
    const node = formRef.current;
    const handleInput = (event) => {
      const target = event.target;
      if (!target || !target.name) return;
      const value = target.type === 'checkbox' ? target.checked : target.value;
      setValues(prev => ({ ...prev, [target.name]: value }));
    };
    node.addEventListener('input', handleInput);
    node.addEventListener('change', handleInput);
    return () => {
      node.removeEventListener('input', handleInput);
      node.removeEventListener('change', handleInput);
    };
  }, [form?.id, isHtmlForm]);

  useEffect(() => {
    let cancelled = false;

    const loadSubmissions = async () => {
      if (!form?.id || !isOwner) {
        setOwnerSubmissions([]);
        setSubmissionsError(null);
        setSubmissionsLoading(false);
        return;
      }

      setSubmissionsLoading(true);
      setSubmissionsError(null);
      try {
        const items = await formService.getFormSubmissions(form.id, 25);
        if (!cancelled) setOwnerSubmissions(items);
      } catch (err) {
        if (!cancelled) {
          setOwnerSubmissions([]);
          setSubmissionsError(err?.message || 'Failed to load submissions.');
        }
      } finally {
        if (!cancelled) setSubmissionsLoading(false);
      }
    };

    loadSubmissions();
    return () => {
      cancelled = true;
    };
  }, [form?.id, isOwner, submissionsRefreshTick]);

  const openShareModal = () => {
    if (!form) return;
    const desc = form?.shareSettings?.description || form?.config?.instructions || '';
    setShareVisibility(form.visibility || 'private');
    setShareEmailsInput(Array.isArray(form.allowedEmails) ? form.allowedEmails.join(', ') : '');
    setShareTitle(form?.shareSettings?.title || form.title || '');
    setShareDescription(desc);
    setShowShareModal(true);
  };

  const handleCopyLink = async () => {
    if (!form) return;
    if (form.visibility === 'private') {
      toast.error('Switch access to public or restricted before sharing a link.');
      return;
    }
    const url = `${window.location.origin}/form/${form.id}`;
    await navigator.clipboard.writeText(url);
    toast.success('Share link copied to clipboard.');
  };

  const handleNativeShare = async () => {
    if (!form) return;
    if (form.visibility === 'private') {
      toast.error('Switch access to public or restricted before sharing a link.');
      return;
    }
    const url = `${window.location.origin}/form/${form.id}`;
    if (!navigator.share) {
      await handleCopyLink();
      return;
    }
    try {
      await navigator.share({
        title: (shareTitle || form.title || 'Shared document').trim(),
        text: (shareDescription || '').trim(),
        url
      });
    } catch (err) {
      console.warn('Share cancelled:', err);
    }
  };

  const saveShareOptions = async () => {
    if (!form || !isOwner) return;
    const nextVisibility = shareVisibility || 'private';
    const nextEmails = nextVisibility === 'restricted' ? parseEmailList(shareEmailsInput) : [];
    if (nextVisibility === 'restricted' && nextEmails.length === 0) {
      toast.error('Add at least one valid email for restricted access.');
      return;
    }

    setIsSavingShare(true);
    try {
      const updates = {
        visibility: nextVisibility,
        allowedEmails: nextEmails,
        shareSettings: {
          title: (shareTitle || form.title || '').trim(),
          description: (shareDescription || '').trim()
        }
      };
      await formService.updateForm(form.id, updates);
      await formService.publishForm(
        form.id,
        {
          ...updates,
          files: { sourcePdf: form.sourcePdf || null },
          smartDocument: form.smartDocument || null,
          originalDocumentId: form.originalDocumentId || form.documentId || form.id
        },
        user?.uid
      );
      setForm((prev) => ({ ...prev, ...updates }));
      toast.success('Share settings updated.');
      setShowShareModal(false);
    } catch (err) {
      toast.error(err?.message || 'Failed to update share settings.');
    } finally {
      setIsSavingShare(false);
    }
  };

  const handleSubmitResponse = async (submitComponentId = null) => {
    if (!form) return;
    if (!validateRequiredSubmission()) return;

    const submittedAtClient = new Date().toISOString();
    const mode = hasSmartDocument ? 'smart' : (isHtmlForm ? 'html' : 'classic');
    const basePayload = {
      formTitle: form.title || '',
      values: valuesRef.current,
      mode,
      submitComponentId: `${submitComponentId || ''}`.trim() || null,
      submittedAtClient
    };

    if (hasSmartDocument) {
      const fields = smartComponents
        .filter((component) => isFillableSmartType(component.type))
        .map((component) => {
          const variableName = component?.props?.variableName || component.id;
          return {
            componentId: component.id,
            variableName,
            type: component.type,
            required: Boolean(component?.props?.required),
            value: valuesRef.current[variableName]
          };
        });

      const smartChips = Object.entries(smartChipValues)
        .map(([componentId, data]) => {
          const component = smartChipComponentMap[componentId];
          if (!component) return null;
          return {
            componentId,
            componentRef: {
              componentId,
              variableName: component?.props?.variableName || componentId
            },
            variableName: component?.props?.variableName || componentId,
            type: component.type,
            data
          };
        })
        .filter(Boolean);

      basePayload.fields = fields;
      basePayload.smartChips = smartChips;
    } else if (!isHtmlForm) {
      basePayload.fields = (form?.fields || []).map((field) => ({
        fieldId: field.id,
        label: field.label,
        type: field.type,
        required: Boolean(field.required),
        value: valuesRef.current[field.id]
      }));
    }

    setIsSubmitting(true);
    try {
      await formService.saveSubmission(form.id, basePayload, user?.uid);
      toast.success('Form submitted successfully.');
      setActiveSmartChipId(null);
      if (isOwner) {
        setSubmissionsRefreshTick((prev) => prev + 1);
      }
    } catch (err) {
      console.error('Submit failed:', err);
      toast.error(err?.message || 'Failed to submit form.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const exportNodeToPdf = async (node, filename, options = {}) => {
    const { standardA4 = false, smartHeaderText = '', smartFooterText = '', showSmartHeader = false, showSmartFooter = false } = options;
    const canvas = await html2canvas(node, {
      scale: 2,
      backgroundColor: '#ffffff',
      useCORS: true,
      imageTimeout: 15000,
      width: node.scrollWidth || node.clientWidth,
      height: node.scrollHeight || node.clientHeight,
      windowWidth: node.scrollWidth || node.clientWidth,
      windowHeight: node.scrollHeight || node.clientHeight,
      onclone: (clonedDoc) => {
        const clonedWindow = clonedDoc.defaultView;
        clonedDoc.querySelectorAll('[data-export-hide="true"], input[type="submit"]').forEach((el) => {
          el.style.display = 'none';
        });
        replaceYoutubeEmbedsForExport(clonedDoc);

        clonedDoc.querySelectorAll('textarea').forEach((el) => {
          el.textContent = el.value || '';
        });

        clonedDoc.querySelectorAll('input, select, textarea').forEach((el) => {
          if (el.type === 'checkbox' || el.type === 'radio') {
            if (el.checked) {
              el.setAttribute('checked', 'checked');
            } else {
              el.removeAttribute('checked');
            }
            return;
          }

          const styles = clonedWindow ? clonedWindow.getComputedStyle(el) : null;
          const replacement = clonedDoc.createElement('div');
          const isTextarea = el.tagName === 'TEXTAREA';
          const isSelect = el.tagName === 'SELECT';
          let valueText = '';
          if (isSelect) {
            const selected = el.options?.[el.selectedIndex];
            valueText = selected ? selected.textContent || '' : '';
          } else {
            valueText = el.value || '';
          }
          replacement.textContent = valueText || el.getAttribute('placeholder') || '';

          replacement.style.boxSizing = 'border-box';
          replacement.style.width = styles?.width || '100%';
          replacement.style.minHeight = styles?.height || '42px';
          replacement.style.height = styles?.height || 'auto';
          replacement.style.padding = styles?.padding || '10px 12px';
          replacement.style.border = styles?.border || '1px solid #d1d5db';
          replacement.style.borderRadius = styles?.borderRadius || '8px';
          replacement.style.background = styles?.backgroundColor || '#ffffff';
          replacement.style.color = valueText ? (styles?.color || '#111827') : '#94a3b8';
          replacement.style.fontFamily = styles?.fontFamily || 'inherit';
          replacement.style.fontSize = styles?.fontSize || '14px';
          replacement.style.fontWeight = styles?.fontWeight || '400';
          replacement.style.lineHeight = styles?.lineHeight === 'normal' ? '1.4' : (styles?.lineHeight || '1.4');
          replacement.style.letterSpacing = styles?.letterSpacing || 'normal';
          replacement.style.display = 'flex';
          replacement.style.alignItems = isTextarea ? 'flex-start' : 'center';
          replacement.style.whiteSpace = isTextarea ? 'pre-wrap' : 'nowrap';
          replacement.style.overflow = 'hidden';
          replacement.style.textOverflow = 'ellipsis';

          el.replaceWith(replacement);
        });
      }
    });
    const imageData = canvas.toDataURL('image/png');
    const pdfDoc = await PDFDocument.create();
    if (!standardA4) {
      const page = pdfDoc.addPage([canvas.width, canvas.height]);
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const image = await pdfDoc.embedPng(imageData);
      page.drawImage(image, { x: 0, y: 0, width: canvas.width, height: canvas.height });
      const brandSize = 11;
      const brandY = 10;
      const brandWidth = font.widthOfTextAtSize(PARROTDOCS_URL, brandSize);
      const brandX = Math.max(0, (canvas.width - brandWidth) / 2);
      page.drawText(PARROTDOCS_URL, {
        x: brandX,
        y: brandY,
        size: brandSize,
        font,
        color: rgb(0.39, 0.45, 0.53)
      });
      addPdfLinkAnnotation(pdfDoc, page, brandX, brandY - 2, brandWidth, brandSize + 5, PARROTDOCS_URL);
    } else {
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const marginX = 36;
      const marginTop = 26;
      const marginBottom = 26;
      const headerGap = showSmartHeader ? 24 : 0;
      const footerGap = showSmartFooter ? 24 : 0;
      const contentX = marginX;
      const contentWidthPt = A4_WIDTH_PT - (marginX * 2);
      const contentTopPt = A4_HEIGHT_PT - marginTop - headerGap;
      const contentBottomPt = marginBottom + footerGap;
      const contentHeightPt = Math.max(60, contentTopPt - contentBottomPt);
      const pageHeightPx = Math.max(1, Math.floor(canvas.width * (contentHeightPt / contentWidthPt)));
      let offsetY = 0;

      while (offsetY < canvas.height) {
        const sliceHeight = Math.min(pageHeightPx, canvas.height - offsetY);
        const sliceCanvas = document.createElement('canvas');
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = sliceHeight;
        const sliceCtx = sliceCanvas.getContext('2d');
        if (!sliceCtx) throw new Error('Failed to prepare PDF page image.');
        sliceCtx.drawImage(
          canvas,
          0,
          offsetY,
          canvas.width,
          sliceHeight,
          0,
          0,
          canvas.width,
          sliceHeight
        );

        const sliceImage = await pdfDoc.embedPng(sliceCanvas.toDataURL('image/png'));
        const page = pdfDoc.addPage([A4_WIDTH_PT, A4_HEIGHT_PT]);
        const drawHeight = contentWidthPt * (sliceHeight / canvas.width);
        page.drawImage(sliceImage, {
          x: contentX,
          y: contentTopPt - drawHeight,
          width: contentWidthPt,
          height: drawHeight
        });

        if (showSmartHeader) {
          page.drawText(`${smartHeaderText || ''}`, {
            x: marginX,
            y: A4_HEIGHT_PT - marginTop,
            size: 9,
            font,
            color: rgb(0.47, 0.52, 0.6)
          });
        }

        if (showSmartFooter) {
          page.drawText(`${smartFooterText || ''}`, {
            x: marginX,
            y: marginBottom - 4,
            size: 9,
            font,
            color: rgb(0.47, 0.52, 0.6)
          });
        }

        const brandSize = 9;
        const brandY = 8;
        const brandWidth = font.widthOfTextAtSize(PARROTDOCS_URL, brandSize);
        const brandX = Math.max(0, (A4_WIDTH_PT - brandWidth) / 2);
        page.drawText(PARROTDOCS_URL, {
          x: brandX,
          y: brandY,
          size: brandSize,
          font,
          color: rgb(0.39, 0.45, 0.53)
        });
        addPdfLinkAnnotation(pdfDoc, page, brandX, brandY - 2, brandWidth, brandSize + 5, PARROTDOCS_URL);
        offsetY += sliceHeight;
      }
    }
    const pdfBytes = await pdfDoc.save();
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  const exportSmartPagesToPdf = async (pageNodes, filename, options = {}) => {
    const { smartHeaderText = '', smartFooterText = '', showSmartHeader = false, showSmartFooter = false } = options;
    const pdfDoc = await PDFDocument.create();
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const marginX = 36;
    const marginTop = 26;
    const marginBottom = 26;
    const headerGap = showSmartHeader ? 24 : 0;
    const contentX = marginX;
    const contentWidthPt = A4_WIDTH_PT - (marginX * 2);
    const contentTopPt = A4_HEIGHT_PT - marginTop - headerGap;

    for (const node of pageNodes) {
      const youtubeTargets = getYoutubeExportTargets(node);
      const canvas = await html2canvas(node, {
        scale: 2,
        backgroundColor: '#ffffff',
        useCORS: true,
        imageTimeout: 15000,
        width: node.scrollWidth || node.clientWidth,
        height: node.scrollHeight || node.clientHeight,
        windowWidth: node.scrollWidth || node.clientWidth,
        windowHeight: node.scrollHeight || node.clientHeight,
        onclone: (clonedDoc) => {
          const clonedWindow = clonedDoc.defaultView;
          clonedDoc.querySelectorAll('[data-export-hide="true"], input[type="submit"]').forEach((el) => {
            el.style.display = 'none';
          });
          replaceYoutubeEmbedsForExport(clonedDoc);

          clonedDoc.querySelectorAll('textarea').forEach((el) => {
            el.textContent = el.value || '';
          });

          clonedDoc.querySelectorAll('input, select, textarea').forEach((el) => {
            if (el.type === 'checkbox' || el.type === 'radio') {
              if (el.checked) {
                el.setAttribute('checked', 'checked');
              } else {
                el.removeAttribute('checked');
              }
              return;
            }

            const styles = clonedWindow ? clonedWindow.getComputedStyle(el) : null;
            const replacement = clonedDoc.createElement('div');
            const isTextarea = el.tagName === 'TEXTAREA';
            const isSelect = el.tagName === 'SELECT';
            let valueText = '';
            if (isSelect) {
              const selected = el.options?.[el.selectedIndex];
              valueText = selected ? selected.textContent || '' : '';
            } else {
              valueText = el.value || '';
            }
            replacement.textContent = valueText || el.getAttribute('placeholder') || '';
            replacement.style.boxSizing = 'border-box';
            replacement.style.width = styles?.width || '100%';
            replacement.style.minHeight = styles?.height || '42px';
            replacement.style.height = styles?.height || 'auto';
            replacement.style.padding = styles?.padding || '10px 12px';
            replacement.style.border = styles?.border || '1px solid #d1d5db';
            replacement.style.borderRadius = styles?.borderRadius || '8px';
            replacement.style.background = styles?.backgroundColor || '#ffffff';
            replacement.style.color = valueText ? (styles?.color || '#111827') : '#94a3b8';
            replacement.style.fontFamily = styles?.fontFamily || 'inherit';
            replacement.style.fontSize = styles?.fontSize || '14px';
            replacement.style.fontWeight = styles?.fontWeight || '400';
            replacement.style.lineHeight = styles?.lineHeight === 'normal' ? '1.4' : (styles?.lineHeight || '1.4');
            replacement.style.letterSpacing = styles?.letterSpacing || 'normal';
            replacement.style.display = 'flex';
            replacement.style.alignItems = isTextarea ? 'flex-start' : 'center';
            replacement.style.whiteSpace = isTextarea ? 'pre-wrap' : 'nowrap';
            replacement.style.overflow = 'hidden';
            replacement.style.textOverflow = 'ellipsis';
            el.replaceWith(replacement);
          });
        }
      });

      const page = pdfDoc.addPage([A4_WIDTH_PT, A4_HEIGHT_PT]);
      const image = await pdfDoc.embedPng(canvas.toDataURL('image/png'));
      const drawHeight = contentWidthPt * (canvas.height / canvas.width);
      const nodeWidth = node.getBoundingClientRect().width || node.scrollWidth || node.clientWidth || A4_WIDTH_PX;
      const pointScale = contentWidthPt / nodeWidth;
      page.drawImage(image, {
        x: contentX,
        y: contentTopPt - drawHeight,
        width: contentWidthPt,
        height: drawHeight
      });

      for (const target of youtubeTargets) {
        const annotationX = contentX + (target.left * pointScale);
        const annotationY = contentTopPt - ((target.top + target.height) * pointScale);
        const annotationWidth = target.width * pointScale;
        const annotationHeight = target.height * pointScale;
        const thumbnailImage = await embedYoutubeThumbnail(pdfDoc, target.id);

        if (thumbnailImage) {
          page.drawImage(thumbnailImage, {
            x: annotationX,
            y: annotationY,
            width: annotationWidth,
            height: annotationHeight
          });
          page.drawEllipse({
            x: annotationX + (annotationWidth / 2),
            y: annotationY + (annotationHeight / 2),
            xScale: 28,
            yScale: 20,
            color: rgb(1, 0, 0)
          });
          page.drawSvgPath('M -7 -10 L -7 10 L 13 0 Z', {
            x: annotationX + (annotationWidth / 2) + 3,
            y: annotationY + (annotationHeight / 2),
            color: rgb(1, 1, 1),
            scale: 1
          });
        }

        addPdfLinkAnnotation(
          pdfDoc,
          page,
          annotationX,
          annotationY,
          annotationWidth,
          annotationHeight,
          target.url
        );
      }

      if (showSmartHeader) {
        page.drawText(`${smartHeaderText || ''}`, {
          x: marginX,
          y: A4_HEIGHT_PT - marginTop,
          size: 9,
          font,
          color: rgb(0.47, 0.52, 0.6)
        });
      }

      if (showSmartFooter) {
        page.drawText(`${smartFooterText || ''}`, {
          x: marginX,
          y: marginBottom - 4,
          size: 9,
          font,
          color: rgb(0.47, 0.52, 0.6)
        });
      }

      const brandSize = 9;
      const brandY = 8;
      const brandWidth = font.widthOfTextAtSize(PARROTDOCS_URL, brandSize);
      const brandX = Math.max(0, (A4_WIDTH_PT - brandWidth) / 2);
      page.drawText(PARROTDOCS_URL, {
        x: brandX,
        y: brandY,
        size: brandSize,
        font,
        color: rgb(0.39, 0.45, 0.53)
      });
      addPdfLinkAnnotation(pdfDoc, page, brandX, brandY - 2, brandWidth, brandSize + 5, PARROTDOCS_URL);
    }

    const pdfBytes = await pdfDoc.save();
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  const syncValuesToDom = (valueMap) => {
    if (!formRef.current) return;
    const inputs = formRef.current.querySelectorAll('input, textarea, select');
    inputs.forEach((input) => {
      const name = input.getAttribute('name');
      if (!name) return;
      const nextValue = valueMap[name];
      if (input.type === 'checkbox') {
        input.checked = Boolean(nextValue);
      } else {
        input.value = nextValue ?? '';
      }
    });
  };

  const syncSmartValuesToDom = (node, valueMap) => {
    if (!node) return;
    const controls = node.querySelectorAll('[data-field-key]');
    controls.forEach((control) => {
      const key = control.getAttribute('data-field-key');
      if (!key) return;
      const nextValue = valueMap[key];
      if (control.tagName === 'INPUT' && control.type === 'checkbox') {
        const checklistOption = control.getAttribute('data-checklist-option');
        if (checklistOption) {
          control.checked = Array.isArray(nextValue) && nextValue.includes(checklistOption);
        } else {
          control.checked = Boolean(nextValue);
        }
        if (control.checked) {
          control.setAttribute('checked', 'checked');
        } else {
          control.removeAttribute('checked');
        }
      } else if (control.tagName === 'INPUT' && control.type === 'radio') {
        control.checked = `${nextValue ?? ''}` === `${control.value ?? ''}`;
        if (control.checked) {
          control.setAttribute('checked', 'checked');
        } else {
          control.removeAttribute('checked');
        }
      } else if (control.tagName === 'SELECT') {
        control.value = nextValue ?? '';
        control.setAttribute('value', control.value);
      } else {
        control.value = nextValue ?? '';
        control.setAttribute('value', control.value);
      }
    });
  };

  const buildFilledValues = () => {
    if (hasSmartDocument) {
      return smartComponents.reduce((acc, component) => {
        const key = component?.props?.variableName || component.id;
        if (!key) return acc;
        const current = valuesRef.current[key];
        const hasCurrentValue =
          current !== undefined &&
          current !== null &&
          !(typeof current === 'string' && current.trim() === '');

        if (component.type === 'checkbox') {
          acc[key] = hasCurrentValue ? Boolean(current) : true;
        } else if (component.type === 'checklist') {
          const options = Array.isArray(component?.props?.options) ? component.props.options : [];
          acc[key] = Array.isArray(current) && current.length > 0 ? current : options;
        } else if (component.type === 'radio') {
          acc[key] = hasCurrentValue ? current : (component?.props?.options?.[0] || '');
        } else if (component.type === 'datePicker') {
          acc[key] = hasCurrentValue ? current : '2026-01-01';
        } else if (component.type === 'rating') {
          acc[key] = hasCurrentValue ? Number(current) : Number(component?.props?.maxRating ?? 5);
        } else if (component.type === 'progress') {
          acc[key] = hasCurrentValue ? Number(current) : 100;
        } else if (component.type === 'trafficLightStatus') {
          acc[key] = hasCurrentValue ? current : 'green';
        } else if (component.type === 'dropdown') {
          acc[key] = hasCurrentValue ? current : (component?.props?.options?.[0] || '');
        } else {
          acc[key] = hasCurrentValue ? current : '________';
        }
        return acc;
      }, {});
    }
    if (!form?.fields) return {};
    return form.fields.reduce((acc, field) => {
      const current = valuesRef.current[field.id];
      if (field.type === 'checkbox') {
        acc[field.id] = current === true;
      } else if (current && current.length > 0) {
        acc[field.id] = current;
      } else if (field.type === 'date') {
        acc[field.id] = 'MM/DD/YYYY';
      } else if (field.type === 'signature') {
        acc[field.id] = 'Signature';
      } else {
        acc[field.id] = '________';
      }
      return acc;
    }, {});
  };

  const captureWithValues = async (mode) => {
    try {
      if (!form) {
        toast.error('Form is not loaded yet.');
        return;
      }
      const targetNode = hasSmartDocument ? smartExportRef.current : formRef.current;
      if (!targetNode) {
        toast.error('Nothing to export yet.');
        return;
      }
      const smartPageNodes = hasSmartDocument
        ? Array.from(targetNode.querySelectorAll('[data-smart-page-content="true"]'))
        : [];
      const current = valuesRef.current;

      let nextValues = current;
      if (mode === 'empty') nextValues = hasSmartDocument ? smartDefaultValues : defaultValues;
      if (mode === 'filled') nextValues = buildFilledValues();

      const hasTempValues = mode !== 'current';
      if (hasTempValues) {
        setValues(nextValues);
        if (isHtmlForm) {
          syncValuesToDom(nextValues);
        }
        await new Promise(resolve => setTimeout(resolve, 120));
      } else if (isHtmlForm) {
        syncValuesToDom(current);
      }

      try {
        if (hasSmartDocument) {
          syncSmartValuesToDom(targetNode, hasTempValues ? nextValues : current);
        }

        const signatureNode = document.createElement('div');
        signatureNode.innerHTML = EXPORT_SIGNATURE_HTML;
        targetNode.appendChild(signatureNode.firstChild);

        if (hasSmartDocument) {
          await new Promise(resolve => setTimeout(resolve, 60));
        }

        if (hasSmartDocument) {
          await exportSmartPagesToPdf(smartPageNodes, `${form.title || 'Form'}_${mode}.pdf`, {
            smartHeaderText: smartDocConfig.headerText,
            smartFooterText: smartDocConfig.footerText,
            showSmartHeader: Boolean(smartDocConfig.showHeader),
            showSmartFooter: Boolean(smartDocConfig.showFooter)
          });
        } else {
          await exportNodeToPdf(targetNode, `${form.title || 'Form'}_${mode}.pdf`, {
            standardA4: false
          });
        }
      } finally {
        targetNode.querySelectorAll('[data-export-signature="true"]').forEach((el) => el.remove());
        if (hasTempValues) {
          setValues(current);
          if (isHtmlForm) {
            syncValuesToDom(current);
          }
        }
      }
    } catch (err) {
      console.error('PDF export failed:', err);
      toast.error(err?.message || 'Failed to export PDF.');
    }
  };

  if (loading) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-gray-500">
        Loading form...
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-8 text-center text-red-700">
        {error}
      </div>
    );
  }

  if (!form) {
    return null;
  }

  return (
    <div className="min-h-screen space-y-6 bg-gradient-to-br from-[#f6f7fb] via-[#eef1ff] to-[#fef7ea] p-4 md:p-6">
      {!isSharedView && (
        <header
          className={`flex flex-wrap items-center justify-between gap-4 ${
            hasSmartDocument && smartDocConfig.showNavigationMenu ? 'lg:ml-80' : ''
          }`}
        >
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="rounded-full border border-gray-200 p-2 text-gray-600 hover:bg-gray-50"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <div>
              <h1 className="text-3xl font-semibold text-gray-900">{form.title}</h1>
              <p className="text-gray-500">{form.fields?.length || 0} fields</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {isOwner && (
              <button
                onClick={() => navigate(`/forms/smart/${form.id}/edit`)}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <Pencil className="h-4 w-4" />
                Edit
              </button>
            )}
            {isOwner && (
              <button
                onClick={openShareModal}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                {form.visibility === 'public' ? <Globe className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
                {form.visibility === 'public' ? 'Public' : form.visibility === 'restricted' ? 'Restricted' : 'Private'}
              </button>
            )}
            <button
              onClick={openShareModal}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Share2 className="h-4 w-4" />
              Share
            </button>
          </div>
        </header>
      )}

      <div
        className={
          hasSmartDocument
            ? 'p-0'
            : 'rounded-2xl border border-gray-200 bg-white p-6 shadow-sm'
        }
      >
        {hasSmartDocument ? (
          <div className="block">
            {smartDocConfig.showNavigationMenu && (
              <SmartNavigationPanel
                items={navigationItems}
                title={smartDocConfig.navigationMenuTitle}
                fixedClass={isSharedView ? 'lg:left-6 lg:top-6 lg:bottom-6' : 'lg:left-6 lg:top-44 lg:bottom-6'}
              />
            )}
            <div className={`min-w-0 overflow-x-auto pb-2 ${smartDocConfig.showNavigationMenu ? 'lg:ml-80' : ''}`}>
              <div ref={smartExportRef} className="space-y-6">
                {paginatedSmartPages.map((pageItems, pageIndex) => (
                  <div
                    key={`smart-page-${pageIndex}`}
                    className="mx-auto border border-slate-200 bg-white shadow-sm"
                    style={{ width: `${A4_WIDTH_PX}px`, height: `${A4_HEIGHT_PX}px`, minWidth: `${A4_WIDTH_PX}px` }}
                  >
                    <div className="flex h-full flex-col bg-white">
                      {smartDocConfig.showHeader && !isSharedView && (
                        <div className="box-border border-b border-slate-100 px-12 pb-2 pt-6 text-xs text-slate-400" style={{ height: `${HEADER_SPACE_PX}px` }}>
                          {smartDocConfig.headerText}
                        </div>
                      )}
                      <div data-smart-page-content="true" className="box-border px-12 pb-[72px] pt-6" style={{ height: `${smartPageContentHeight}px`, overflow: 'hidden' }}>
                        <div className="grid grid-cols-12 gap-2">
                          {pageItems.map((item) => {
                            const component = item.component;
                            const renderComponent = item.renderComponent;
                            const key = component?.props?.variableName || component.id;
                            const value = values[key];
                            const chipConfig = component?.props?.smartChipConfig || {};
                            const chipValue = smartChipValues[component.id] || component?.props?.smartChipData || {};
                            const sectionAnchorId = ['heading', 'sectionDivider'].includes(component?.type)
                              ? `smart-section-${component.id}`
                              : undefined;

                            return (
                              <div
                                key={item.key}
                                id={sectionAnchorId}
                                className={`relative rounded-xl border border-transparent scroll-mt-24 ${getWidthClass(component?.props?.width)}`}
                              >
                                <SmartDocComponent
                                  component={renderComponent}
                                  fieldKey={key}
                                  value={value}
                                  onValueChange={(next) => handleChange(key, next)}
                                  onSubmit={handleSubmitResponse}
                                  submitting={isSubmitting}
                                  readOnly={!isFillableSmartType(component.type)}
                                />
                                {chipConfig?.enabled && (
                                  <button
                                    type="button"
                                    data-export-hide="true"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setActiveSmartChipId((prev) => (prev === component.id ? null : component.id));
                                    }}
                                    className={`absolute z-20 rounded-full border px-2 py-1 text-[10px] font-bold shadow-sm ${
                                      isSmartChipFilled(chipValue)
                                        ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                                        : 'border-slate-200 bg-white text-slate-400 hover:border-[#4f46e5] hover:text-[#232753]'
                                    } ${
                                      chipConfig?.chipPosition === 'top-left'
                                        ? '-left-2 -top-2'
                                        : chipConfig?.chipPosition === 'top-right'
                                          ? '-right-2 -top-2'
                                          : chipConfig?.chipPosition === 'bottom-left'
                                            ? '-bottom-2 -left-2'
                                            : '-bottom-2 -right-2'
                                    }`}
                                  >
                                    {chipConfig?.chipStyle === 'icon-only'
                                      ? 'SC'
                                      : chipConfig?.chipStyle === 'minimal'
                                        ? 'Smart'
                                        : 'Smart Chip'}
                                  </button>
                                )}
                                {activeSmartChipId === component.id && chipConfig?.enabled && (
                                  <SmartChipModal
                                    config={chipConfig}
                                    initialData={chipValue}
                                    onSave={(data) => {
                                      setSmartChipValues((prev) => ({ ...prev, [component.id]: data }));
                                      setActiveSmartChipId(null);
                                    }}
                                    onClose={() => setActiveSmartChipId(null)}
                                  />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      {smartDocConfig.showFooter && (
                        <div className="box-border border-t border-slate-100 px-12 pb-6 pt-2 text-xs text-slate-400" style={{ height: `${FOOTER_SPACE_PX}px` }}>
                          {smartDocConfig.footerText}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : isHtmlForm ? (
          <div className="space-y-4">
            {!htmlHasInputs && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                No fields were detected in the generated HTML. Try converting again with a clearer layout or switch to
                “Original Layout”.
              </div>
            )}
            <div ref={formRef} dangerouslySetInnerHTML={{ __html: `${htmlStyles}${htmlContent}` }} />
          </div>
        ) : (
          <div ref={formRef} className="space-y-6">
            <div className="border-b border-gray-200 pb-4">
              <h2 className="text-xl font-semibold text-gray-900">{form.title}</h2>
              {form.config?.instructions && (
                <p className="mt-2 text-sm text-gray-600">{form.config.instructions}</p>
              )}
            </div>

            <div className="grid gap-4">
              {form.fields?.map(field => (
                <label key={field.id} className="space-y-2 text-sm text-gray-700">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">{field.label}</span>
                    {field.required && <span className="text-xs text-red-500">Required</span>}
                  </div>
                  {field.type === 'checkbox' ? (
                    <input
                      type="checkbox"
                      checked={values[field.id] || false}
                      onChange={(e) => handleChange(field.id, e.target.checked)}
                      className="h-4 w-4 rounded border-gray-400 text-indigo-600 focus:ring-indigo-500"
                    />
                  ) : (
                    <input
                      type={field.type === 'date' ? 'date' : 'text'}
                      value={values[field.id] || ''}
                      onChange={(e) => handleChange(field.id, e.target.value)}
                      placeholder={field.placeholder || ''}
                      required={field.required}
                      className="w-full rounded-lg border border-gray-400 px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                    />
                  )}
                </label>
              ))}
            </div>
          </div>
        )}

        {isSharedView && (
          <div className="mt-6 border-t border-slate-200 pt-4 text-center text-xs text-slate-500">
            Created with{' '}
            <a
              href="https://parrotdocs.co.za/forms"
              target="_blank"
              rel="noreferrer"
              className="font-semibold text-[#232753] underline hover:text-[#1b1f47]"
            >
              ParrotDocs
            </a>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        {(!hasSmartDocument || !hasSmartSubmitButton) && (
          <button
            onClick={() => handleSubmitResponse()}
            disabled={isSubmitting}
            className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white ${
              isSubmitting ? 'bg-gray-400' : 'bg-[#232753] hover:bg-[#1b1f47]'
            }`}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Response'}
          </button>
        )}
        <button
          onClick={() => captureWithValues('empty')}
          className="inline-flex items-center gap-2 rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
        >
          <Download className="h-4 w-4" />
          Download Empty
        </button>
        <button
          onClick={() => captureWithValues('current')}
          className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
        >
          <Download className="h-4 w-4" />
          Download Partial
        </button>
        <button
          onClick={() => captureWithValues('filled')}
          className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700"
        >
          <Download className="h-4 w-4" />
          Download Filled
        </button>
      </div>

      {isOwner && (
        <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center justify-between gap-3">
            <h2 className="text-lg font-semibold text-gray-900">Submissions</h2>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
              {ownerSubmissions.length} total
            </span>
          </div>

          {submissionsLoading && (
            <p className="text-sm text-slate-500">Loading submissions...</p>
          )}

          {!submissionsLoading && submissionsError && (
            <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {submissionsError}
            </p>
          )}

          {!submissionsLoading && !submissionsError && ownerSubmissions.length === 0 && (
            <p className="text-sm text-slate-500">No submissions yet.</p>
          )}

          {!submissionsLoading && !submissionsError && ownerSubmissions.length > 0 && (
            <div className="space-y-3">
              {ownerSubmissions.map((submission, index) => {
                const smartChips = Array.isArray(submission.smartChips) ? submission.smartChips : [];
                return (
                  <details key={submission.id} className="rounded-xl border border-slate-200 bg-slate-50/50 p-3">
                    <summary className="cursor-pointer text-sm font-semibold text-slate-800">
                      Response {ownerSubmissions.length - index} · {formatSubmissionDate(submission.createdAt, submission.submittedAtClient)}
                    </summary>
                    <div className="mt-3 space-y-3 text-sm text-slate-700">
                      <div>
                        <span className="font-semibold text-slate-900">submitComponentId:</span>{' '}
                        <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">
                          {submission.submitComponentId || 'none'}
                        </code>
                      </div>
                      <div>
                        <span className="font-semibold text-slate-900">Smart chips:</span>{' '}
                        <span>{smartChips.length}</span>
                      </div>
                      {smartChips.length > 0 && (
                        <div className="space-y-2">
                          {smartChips.map((chip, chipIndex) => (
                            <div key={`${submission.id}-chip-${chipIndex}`} className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                              <div>
                                <span className="font-semibold text-slate-900">componentRef.componentId:</span>{' '}
                                <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">
                                  {chip?.componentRef?.componentId || chip?.componentId || 'n/a'}
                                </code>
                              </div>
                              <div>
                                <span className="font-semibold text-slate-900">componentRef.variableName:</span>{' '}
                                <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">
                                  {chip?.componentRef?.variableName || chip?.variableName || 'n/a'}
                                </code>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </details>
                );
              })}
            </div>
          )}
        </section>
      )}

      {showShareModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Share Options</h2>
              <button
                onClick={() => setShowShareModal(false)}
                className="rounded-lg border border-gray-200 px-3 py-1 text-sm text-gray-600 hover:bg-gray-50"
              >
                Close
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Share Link</label>
                <div className="flex gap-2">
                  <input
                    readOnly
                    value={`${window.location.origin}/form/${form.id}`}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700"
                  />
                  <button
                    onClick={handleCopyLink}
                    className="rounded-lg bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
                  >
                    Copy
                  </button>
                  <button
                    onClick={handleNativeShare}
                    className="rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700"
                  >
                    Share
                  </button>
                </div>
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Share Title</label>
                <input
                  value={shareTitle}
                  onChange={(e) => setShareTitle(e.target.value)}
                  disabled={!isOwner}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 disabled:bg-gray-50"
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Share Description</label>
                <textarea
                  rows={3}
                  value={shareDescription}
                  onChange={(e) => setShareDescription(e.target.value)}
                  disabled={!isOwner}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 disabled:bg-gray-50"
                />
              </div>

              {isOwner && (
                <>
                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Access</label>
                    <select
                      value={shareVisibility}
                      onChange={(e) => setShareVisibility(e.target.value)}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700"
                    >
                      <option value="private">Private (owner only)</option>
                      <option value="restricted">Restricted (specific emails)</option>
                      <option value="public">Public (anyone with link)</option>
                    </select>
                  </div>

                  {shareVisibility === 'restricted' && (
                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Allowed Emails</label>
                      <input
                        value={shareEmailsInput}
                        onChange={(e) => setShareEmailsInput(e.target.value)}
                        placeholder="name@company.com, second@company.com"
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700"
                      />
                    </div>
                  )}
                </>
              )}
            </div>

            {isOwner && (
              <div className="mt-6 flex justify-end">
                <button
                  onClick={saveShareOptions}
                  disabled={isSavingShare}
                  className={`rounded-lg px-4 py-2 text-sm font-semibold text-white ${
                    isSavingShare ? 'bg-gray-400' : 'bg-[#232753] hover:bg-[#1b1f47]'
                  }`}
                >
                  {isSavingShare ? 'Saving...' : 'Save Share Settings'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FormViewer;
